import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BookOpen, Heart, Star, Palette, Waves, X, Sparkles, Trophy, Route, CheckCircle, Trees, Shield, Compass, Footprints, Volume2, VolumeX, Moon, User, ChevronLeft, ChevronRight, Gamepad2, Coffee } from 'lucide-react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import api, { questsAPI, learningPathsAPI, completionsAPI, progressAPI, analyticsAPI } from '../api';
import { AvatarPreview } from '../components/AvatarBuilder';
import { FullBodyAvatarPreview } from '../components/FullBodyAvatar';
import { FeedbackButton } from '../components/FeedbackButton';
import FirstTimeOnboarding from '../components/FirstTimeOnboarding';
import { toast } from 'sonner';
import useKeyboardNavigation from '../hooks/useKeyboardNavigation';

// Rank icons mapping
const RANK_ICONS = {
  sparkle: Sparkles,
  compass: Compass,
  footprints: Footprints,
  star: Star,
  shield: Shield
};

// Portal data with room IDs matching backend
const PORTAL_DATA = [
  // Enhanced portal colors for stronger visual contrast - Beta Ready
  // Gold #FFD700, Cyan #00E5FF, Emerald #3BFF9C, Amber #FF9E3D, Violet #B26BFF
  { id: 'memory_garden', name: 'Memory Garden', position: [0, 2.2, 0], color: 0x3BFF9C, icon: BookOpen, description: 'Read stories and learn new things' },          // Center - Emerald
  { id: 'radiant_will', name: 'Chamber of Radiant Will', position: [-6, 2.2, 2], color: 0xFFD700, icon: Heart, description: 'Build strength and character' },   // Left-front - Gold
  { id: 'star_map', name: 'Star Map Hall', position: [6, 2.2, 2], color: 0x00E5FF, icon: Star, description: 'Explore numbers and patterns' },                   // Right-front - Cyan
  { id: 'creative_forge', name: 'Creative Forge', position: [4, 2.2, -4], color: 0xFF9E3D, icon: Palette, description: 'Make art and music' },        // Right-back - Amber
  { id: 'calm_cove', name: 'Calm Cove', position: [-4, 2.2, -4], color: 0xB26BFF, icon: Waves, description: 'Relax and feel peaceful' },                    // Left-back - Violet
  // New portals for unified navigation
  { id: 'courtyard', name: 'Courtyard', position: [-3, 2.2, 3], color: 0x4ADE80, icon: Gamepad2, isDirectNav: true, navPath: '/courtyard', description: 'Play games and challenges' },  // Green
  { id: 'recess', name: 'Recess', position: [3, 2.2, 3], color: 0xF472B6, icon: Coffee, isDirectNav: true, navPath: '/recess', description: 'Free play and fun activities' }   // Pink
];

// Life path to recommended portal mapping
const LIFE_PATH_PORTALS = {
  1: ['creative_forge', 'radiant_will'],
  2: ['calm_cove', 'memory_garden'],
  3: ['creative_forge', 'star_map'],
  4: ['radiant_will', 'memory_garden'],
  5: ['star_map', 'creative_forge'],
  6: ['calm_cove', 'memory_garden'],
  7: ['star_map', 'memory_garden'],
  8: ['radiant_will', 'creative_forge'],
  9: ['memory_garden', 'calm_cove']
};

const PORTAL_NAMES = {
  memory_garden: 'Memory Garden',
  radiant_will: 'Chamber of Radiant Will',
  star_map: 'Star Map Hall',
  creative_forge: 'Creative Forge',
  calm_cove: 'Calm Cove',
  courtyard: 'Courtyard',
  recess: 'Recess'
};

// Onboarding steps for first-time users
const ONBOARDING_STEPS = [
  {
    title: "Welcome to the Temple! 🏛️",
    description: "This is your magical learning space. Let's take a quick tour!",
    highlight: null, // No specific element highlighted for intro
    position: 'center'
  },
  {
    title: "Learning Portals 📚",
    description: "Tap any glowing portal to start learning! Each one has different lessons and adventures.",
    highlight: 'portal-nav',
    position: 'top'
  },
  {
    title: "Courtyard 🎮",
    description: "Play games like Chess and create your own games in Game Forge!",
    highlight: 'portal-nav-courtyard',
    position: 'top'
  },
  {
    title: "Spark Store ⭐",
    description: "Earn sparks by learning and playing, then spend them on cool rewards!",
    highlight: 'spark-store-btn',
    position: 'left'
  },
  {
    title: "Ready to Explore! 🚀",
    description: "You're all set! Tap a portal to begin your first quest.",
    highlight: null,
    position: 'center',
    isFinal: true
  }
];

const TemplePage = () => {
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loadingLessons, setLoadingLessons] = useState(false);
  const [hoveredPortal, setHoveredPortal] = useState(null);
  const [guidanceMessage, setGuidanceMessage] = useState(null);
  const [canvasKey, setCanvasKey] = useState(0); // For forcing remount
  const [isNavigating, setIsNavigating] = useState(false); // Prevent re-renders during navigation
  
  // TV Navigation state
  const [tvNavMode, setTvNavMode] = useState(false);
  const [tvFocusedPortalIndex, setTvFocusedPortalIndex] = useState(0);
  const portalNavRef = useRef(null);
  
  // Touch device detection
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  
  // Onboarding state - only show for first-time users (NEW comprehensive flow)
  const [showFirstTimeOnboarding, setShowFirstTimeOnboarding] = useState(false);
  // Legacy onboarding state (keep for backward compatibility, will phase out)
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  
  // Detect touch device on mount
  useEffect(() => {
    const checkTouch = () => {
      setIsTouchDevice(
        'ontouchstart' in window || 
        navigator.maxTouchPoints > 0 ||
        window.matchMedia('(pointer: coarse)').matches
      );
    };
    checkTouch();
    window.addEventListener('resize', checkTouch);
    return () => window.removeEventListener('resize', checkTouch);
  }, []);
  
  // Quest system state
  const [dailyQuests, setDailyQuests] = useState([]);
  const [sparks, setSparks] = useState({ knowledge: 0, creativity: 0, discovery: 0, calm: 0, willpower: 0 });
  const [activeQuestId, setActiveQuestId] = useState(null); // Track which quest the user is working on
  
  // Daily Quest Banner state
  const [showQuestBanner, setShowQuestBanner] = useState(false);
  const [questBannerDismissed, setQuestBannerDismissed] = useState(false);
  
  // Falling Knowledge Star state
  const [fallingStarActive, setFallingStarActive] = useState(false);
  const [fallingStarPosition, setFallingStarPosition] = useState({ x: 0, y: 0 });
  const [fallingStarChallenge, setFallingStarChallenge] = useState(null);
  const [fallingStarCaught, setFallingStarCaught] = useState(false);
  const fallingStarTimerRef = useRef(null);
  
  // Rank state
  const [rank, setRank] = useState(null);
  
  // Learning paths state
  const [learningPaths, setLearningPaths] = useState([]);
  const [completedLessons, setCompletedLessons] = useState([]);
  
  // Soundscape state
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('templeSoundEnabled') === 'true';
  });
  const [soundVolume, setSoundVolume] = useState(() => {
    return parseFloat(localStorage.getItem('templeSoundVolume') || '0.3');
  });
  const audioContextRef = useRef(null);
  const ambientGainRef = useRef(null);
  const ambientOscillatorsRef = useRef([]);
  
  const navigate = useNavigate();
  
  // Refs for Three.js objects - will be populated in onCreated
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const animationIdRef = useRef(null);
  const isUnmountedRef = useRef(false);
  const isSceneInitializedRef = useRef(false); // Prevent duplicate initialization
  const portalMeshesRef = useRef([]);
  const portalVisualsRef = useRef([]);
  const canvasContainerRef = useRef(null);
  
  // Raycaster for mouse interactions
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseRef = useRef(new THREE.Vector2());

  // ============ SOUNDSCAPE FUNCTIONS ============
  
  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  // Play a gentle chime sound (for orb messages, milestones)
  const playChime = useCallback(() => {
    if (!soundEnabled) return;
    const ctx = getAudioContext();
    
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
    oscillator.frequency.setValueAtTime(659.25, ctx.currentTime + 0.1); // E5
    oscillator.frequency.setValueAtTime(783.99, ctx.currentTime + 0.2); // G5
    
    gainNode.gain.setValueAtTime(soundVolume * 0.3, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.8);
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.8);
  }, [soundEnabled, soundVolume, getAudioContext]);

  // Play portal shimmer sound (when hovering/selecting portal)
  const playPortalShimmer = useCallback(() => {
    if (!soundEnabled) return;
    const ctx = getAudioContext();
    
    // Create a brief shimmer effect with multiple frequencies
    const frequencies = [400, 600, 800];
    
    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(freq * 1.2, ctx.currentTime + 0.15);
      
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(soundVolume * 0.15, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(ctx.currentTime + i * 0.03);
      osc.stop(ctx.currentTime + 0.3 + i * 0.03);
    });
  }, [soundEnabled, soundVolume, getAudioContext]);

  // Play sparkle sound (for quest completion, constellation done)
  const playSparkle = useCallback(() => {
    if (!soundEnabled) return;
    const ctx = getAudioContext();
    
    // Create ascending sparkle tones
    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
    
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      
      gain.gain.setValueAtTime(soundVolume * 0.2, ctx.currentTime + i * 0.08);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + i * 0.08 + 0.2);
      
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.start(ctx.currentTime + i * 0.08);
      osc.stop(ctx.currentTime + i * 0.08 + 0.3);
    });
  }, [soundEnabled, soundVolume, getAudioContext]);

  // Handle portal selection - navigates directly for Courtyard/Recess, shows detail for others
  const handlePortalSelect = useCallback((portal) => {
    playPortalShimmer();
    
    // Direct navigation portals (Courtyard, Recess)
    if (portal.isDirectNav && portal.navPath) {
      setIsNavigating(true);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      setTimeout(() => navigate(portal.navPath), 50);
      return;
    }
    
    // Regular portals - show lesson detail
    setSelectedPortal(portal);
  }, [playPortalShimmer, navigate]);

  // NEW Onboarding completion handler - navigates to selected path
  const handleFirstTimeOnboardingComplete = useCallback((selectedPath, selectedDifficulty) => {
    setShowFirstTimeOnboarding(false);
    
    // Navigate to the selected portal
    if (selectedPath) {
      const portal = PORTAL_DATA.find(p => p.id === selectedPath);
      if (portal) {
        handlePortalSelect(portal);
      }
    }
  }, [handlePortalSelect]);

  const handleFirstTimeOnboardingSkip = useCallback(() => {
    setShowFirstTimeOnboarding(false);
  }, []);

  // Legacy Onboarding handlers (keeping for backward compatibility)
  const handleOnboardingNext = useCallback(() => {
    if (onboardingStep < ONBOARDING_STEPS.length - 1) {
      setOnboardingStep(prev => prev + 1);
    } else {
      // Complete onboarding
      if (selectedChild) {
        localStorage.setItem(`temple_onboarding_${selectedChild.id}`, 'true');
      }
      setShowOnboarding(false);
    }
  }, [onboardingStep, selectedChild]);

  const handleOnboardingSkip = useCallback(() => {
    if (selectedChild) {
      localStorage.setItem(`temple_onboarding_${selectedChild.id}`, 'true');
    }
    setShowOnboarding(false);
  }, [selectedChild]);

  // ============ FALLING KNOWLEDGE STAR SYSTEM ============
  const MINI_CHALLENGES = [
    { type: 'math', question: 'What is 7 + 5?', options: ['10', '12', '14', '11'], answer: '12' },
    { type: 'math', question: 'What is 9 - 4?', options: ['3', '5', '6', '4'], answer: '5' },
    { type: 'math', question: 'What is 3 × 4?', options: ['10', '12', '7', '14'], answer: '12' },
    { type: 'pattern', question: 'What comes next? 🔴🔵🔴🔵🔴?', options: ['🔴', '🔵', '🟢', '🟡'], answer: '🔵' },
    { type: 'pattern', question: 'What comes next? ⭐⭐🌙⭐⭐🌙?', options: ['⭐', '🌙', '☀️', '🌟'], answer: '⭐' },
    { type: 'word', question: 'Which word means "happy"?', options: ['Sad', 'Joyful', 'Angry', 'Tired'], answer: 'Joyful' },
    { type: 'word', question: 'What is the opposite of "big"?', options: ['Huge', 'Large', 'Small', 'Tall'], answer: 'Small' },
    { type: 'rhythm', question: 'Tap the beat! 🥁🥁_🥁', options: ['🥁', '🎵', '🔔', '🎶'], answer: '🥁' },
  ];
  
  const spawnFallingStar = useCallback(() => {
    if (fallingStarActive || !selectedChild) return;
    
    // Random starting position at top of screen
    const startX = 20 + Math.random() * 60; // 20-80% of screen width
    
    setFallingStarPosition({ x: startX, y: -10 });
    setFallingStarActive(true);
    setFallingStarCaught(false);
    
    // Select random challenge
    const challenge = MINI_CHALLENGES[Math.floor(Math.random() * MINI_CHALLENGES.length)];
    setFallingStarChallenge({ ...challenge, id: Date.now() });
    
    // Star disappears after 6 seconds if not caught
    setTimeout(() => {
      setFallingStarActive(false);
      setFallingStarChallenge(null);
    }, 6000);
  }, [fallingStarActive, selectedChild]);
  
  const handleCatchFallingStar = useCallback(() => {
    if (!fallingStarActive) return;
    setFallingStarCaught(true);
    playSparkle();
  }, [fallingStarActive, playSparkle]);
  
  const handleChallengeAnswer = useCallback(async (answer) => {
    if (!fallingStarChallenge) return;
    
    const isCorrect = answer === fallingStarChallenge.answer;
    
    if (isCorrect) {
      toast.success('Correct! +3 bonus sparks! ⭐');
      playSparkle();
      
      // Award sparks
      if (selectedChild) {
        try {
          await questsAPI.awardSparks(selectedChild.id, { sparks: 3, type: 'discovery' });
          setSparks(prev => ({ ...prev, discovery: (prev.discovery || 0) + 3 }));
        } catch (err) {
          console.log('Could not award sparks:', err);
        }
      }
    } else {
      toast.error('Not quite, but great try!');
    }
    
    // Close challenge
    setFallingStarActive(false);
    setFallingStarCaught(false);
    setFallingStarChallenge(null);
  }, [fallingStarChallenge, selectedChild, playSparkle]);
  
  // Schedule falling stars every 2-4 minutes
  useEffect(() => {
    if (!selectedChild) return;
    
    const scheduleNextStar = () => {
      const delay = (120 + Math.random() * 120) * 1000; // 2-4 minutes
      fallingStarTimerRef.current = setTimeout(() => {
        spawnFallingStar();
        scheduleNextStar();
      }, delay);
    };
    
    // Start the cycle after a short initial delay
    const initialDelay = setTimeout(() => {
      scheduleNextStar();
    }, 30000); // First star after 30 seconds
    
    return () => {
      clearTimeout(initialDelay);
      if (fallingStarTimerRef.current) {
        clearTimeout(fallingStarTimerRef.current);
      }
    };
  }, [selectedChild, spawnFallingStar]);

  // Start ambient background (soft drone)
  const startAmbient = useCallback(() => {
    if (!soundEnabled || ambientOscillatorsRef.current.length > 0) return;
    
    const ctx = getAudioContext();
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(soundVolume * 0.15, ctx.currentTime);
    masterGain.connect(ctx.destination);
    ambientGainRef.current = masterGain;
    
    // Create a soft ambient drone with multiple oscillators
    const ambientFreqs = [65.41, 98.00, 130.81, 196.00]; // C2, G2, C3, G3
    
    ambientFreqs.forEach(freq => {
      const osc = ctx.createOscillator();
      const oscGain = ctx.createGain();
      
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, ctx.currentTime);
      
      // Add very slow frequency wobble for ethereal feel
      osc.frequency.setValueAtTime(freq * 0.99, ctx.currentTime + 2);
      osc.frequency.setValueAtTime(freq * 1.01, ctx.currentTime + 4);
      osc.frequency.setValueAtTime(freq, ctx.currentTime + 6);
      
      oscGain.gain.setValueAtTime(0.25, ctx.currentTime);
      
      osc.connect(oscGain);
      oscGain.connect(masterGain);
      
      osc.start();
      ambientOscillatorsRef.current.push(osc);
    });
  }, [soundEnabled, soundVolume, getAudioContext]);

  // Stop ambient
  const stopAmbient = useCallback(() => {
    ambientOscillatorsRef.current.forEach(osc => {
      try {
        osc.stop();
      } catch (e) {}
    });
    ambientOscillatorsRef.current = [];
    ambientGainRef.current = null;
  }, []);

  // Toggle sound and persist preference
  const toggleSound = useCallback(() => {
    const newState = !soundEnabled;
    setSoundEnabled(newState);
    localStorage.setItem('templeSoundEnabled', String(newState));
    
    if (newState) {
      startAmbient();
    } else {
      stopAmbient();
    }
  }, [soundEnabled, startAmbient, stopAmbient]);

  // Update volume and persist
  const updateVolume = useCallback((newVolume) => {
    setSoundVolume(newVolume);
    localStorage.setItem('templeSoundVolume', String(newVolume));
    
    if (ambientGainRef.current) {
      const ctx = audioContextRef.current;
      if (ctx) {
        ambientGainRef.current.gain.setValueAtTime(newVolume * 0.15, ctx.currentTime);
      }
    }
  }, []);

  // Start ambient when sound is enabled
  useEffect(() => {
    if (soundEnabled) {
      const timer = setTimeout(() => startAmbient(), 500);
      return () => clearTimeout(timer);
    } else {
      stopAmbient();
    }
  }, [soundEnabled, startAmbient, stopAmbient]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      stopAmbient();
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
    };
  }, [stopAmbient]);

  // Load child data on mount
  useEffect(() => {
    isUnmountedRef.current = false;
    isSceneInitializedRef.current = false; // Reset on mount
    
    const child = localStorage.getItem('selectedChild');
    if (!child) {
      navigate('/child-select');
      return;
    }
    
    try {
      const parsedChild = JSON.parse(child);
      setSelectedChild(parsedChild);
      
      // Check if this child has completed the NEW comprehensive onboarding
      const newOnboardingKey = `onboarding_complete_${parsedChild.id}`;
      const hasCompletedNewOnboarding = localStorage.getItem(newOnboardingKey);
      
      // Also check legacy onboarding key for backward compatibility
      const legacyOnboardingKey = `temple_onboarding_${parsedChild.id}`;
      const hasCompletedLegacyOnboarding = localStorage.getItem(legacyOnboardingKey);
      
      if (!hasCompletedNewOnboarding && !hasCompletedLegacyOnboarding) {
        // Show the NEW comprehensive onboarding for first-time users
        setTimeout(() => {
          setShowFirstTimeOnboarding(true);
        }, 800); // Brief delay to let the scene load
      }
      
      // Fetch daily quests and sparks
      fetchQuestsAndSparks(parsedChild.id, parsedChild.name);
      
    } catch (error) {
      console.error('Error parsing child data:', error);
      navigate('/child-select');
    }
    
    // Cleanup on unmount
    return () => {
      console.log('🧹 TemplePage unmounting - cleaning up');
      isUnmountedRef.current = true;
      
      // Cancel any pending animation frame
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
      
      // Dispose Three.js resources
      if (sceneRef.current) {
        sceneRef.current.traverse((object) => {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(m => m.dispose());
            } else {
              object.material.dispose();
            }
          }
        });
      }
      
      // Clear refs
      portalMeshesRef.current = [];
      portalVisualsRef.current = [];
      sceneRef.current = null;
      cameraRef.current = null;
      isSceneInitializedRef.current = false;
    };
  }, [navigate]);

  // Fetch quests and sparks
  const fetchQuestsAndSparks = async (childId, childName) => {
    try {
      // Fetch daily quests
      const questsRes = await questsAPI.getDailyQuests(childId);
      const allQuests = questsRes.data?.quests || [];
      // Deduplicate quests by quest_id
      const quests = [...new Map(allQuests.map(q => [q.quest_id, q])).values()];
      
      if (!isUnmountedRef.current) {
        setDailyQuests(quests);
        
        // Count incomplete quests
        const incompleteQuests = quests.filter(q => !q.is_completed).length;
        
        // Show daily quest banner if there are incomplete quests and not dismissed today
        const dismissedToday = localStorage.getItem(`questBannerDismissed_${new Date().toDateString()}`);
        if (incompleteQuests > 0 && !dismissedToday) {
          setTimeout(() => setShowQuestBanner(true), 2000); // Show after 2 seconds
        }
        
        // Generate guidance message based on quests
        if (incompleteQuests > 0) {
          setGuidanceMessage(`${childName}, ${incompleteQuests} quest${incompleteQuests > 1 ? 's' : ''} await${incompleteQuests === 1 ? 's' : ''} you today. Choose a portal to begin!`);
        } else if (quests.length > 0) {
          setGuidanceMessage(`${childName}, you've completed all your quests today! Feel free to explore.`);
        } else {
          setGuidanceMessage(`Welcome, ${childName}. Your daily quests are ready.`);
        }
      }
      
      // Fetch sparks
      const sparksRes = await questsAPI.getSparks(childId);
      if (!isUnmountedRef.current && sparksRes.data?.sparks) {
        setSparks(sparksRes.data.sparks);
      }
      
      // Fetch rank
      try {
        const rankRes = await progressAPI.getRank(childId);
        if (!isUnmountedRef.current && rankRes.data) {
          setRank(rankRes.data);
        }
      } catch (e) {
        console.log('Rank not available');
      }
      
      // Fetch learning paths
      try {
        const pathsRes = await learningPathsAPI.getAll();
        if (!isUnmountedRef.current) {
          setLearningPaths(pathsRes.data || []);
        }
      } catch (e) {
        console.log('Learning paths not available');
      }
      
      // Fetch completed lessons for this child
      try {
        const progressRes = await completionsAPI.getProgress(childId);
        if (!isUnmountedRef.current && progressRes.data?.completed_lessons) {
          setCompletedLessons(progressRes.data.completed_lessons.map(l => l.lesson_id));
        }
      } catch (e) {
        console.log('Progress not available');
      }
    } catch (error) {
      console.error('Error fetching quests:', error);
      // Fall back to life path based guidance
      const child = JSON.parse(localStorage.getItem('selectedChild') || '{}');
      if (child.life_path) {
        const recommendedIds = LIFE_PATH_PORTALS[child.life_path] || ['memory_garden'];
        const recommendedName = PORTAL_NAMES[recommendedIds[0]];
        setGuidanceMessage(`${child.name}, explorers like you often begin in the ${recommendedName}.`);
      }
    }
  };

  // Fetch lessons when portal is selected
  useEffect(() => {
    if (selectedPortal && !isNavigating) {
      console.log('🚪 Portal selected:', selectedPortal.name);
      setLoadingLessons(true);
      api.get(`/lessons/published/${selectedPortal.id}`)
        .then(response => {
          if (!isUnmountedRef.current) {
            // Deduplicate lessons by ID first, then by title to remove content duplicates
            const allLessons = response.data || [];
            const byId = [...new Map(allLessons.map(l => [l.id, l])).values()];
            const uniqueLessons = [...new Map(byId.map(l => [l.title.toLowerCase().trim(), l])).values()];
            console.log('📚 Lessons loaded:', uniqueLessons.length, '(deduped from', allLessons.length, ')');
            setLessons(uniqueLessons);
          }
        })
        .catch(error => {
          console.error('Error fetching lessons:', error);
          if (!isUnmountedRef.current) {
            setLessons([]);
          }
        })
        .finally(() => {
          if (!isUnmountedRef.current) {
            setLoadingLessons(false);
          }
        });
    }
  }, [selectedPortal, isNavigating]);

  // ============ TV/REMOTE NAVIGATION ============
  // Detect keyboard navigation and show portal selector
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Activate TV nav mode on arrow key press (when no modal is open)
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key) && !selectedPortal) {
        if (!tvNavMode) {
          setTvNavMode(true);
          // Focus the first portal
          setTimeout(() => {
            const firstPortalBtn = document.querySelector('[data-portal-nav]');
            if (firstPortalBtn) firstPortalBtn.focus();
          }, 100);
        }
      }
      
      // Handle portal navigation with arrow keys
      if (tvNavMode && !selectedPortal) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          setTvFocusedPortalIndex(prev => (prev - 1 + PORTAL_DATA.length) % PORTAL_DATA.length);
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          setTvFocusedPortalIndex(prev => (prev + 1) % PORTAL_DATA.length);
        } else if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const portal = PORTAL_DATA[tvFocusedPortalIndex];
          if (portal) {
            handlePortalSelect(portal);
          }
        } else if (e.key === 'Escape' || e.key === 'Backspace') {
          setTvNavMode(false);
        }
      }
      
      // Close portal detail with Escape/Back
      if (selectedPortal && (e.key === 'Escape' || e.key === 'Backspace')) {
        e.preventDefault();
        setSelectedPortal(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tvNavMode, tvFocusedPortalIndex, selectedPortal, playPortalShimmer]);
  
  // ============ GAMEPAD/CONTROLLER SUPPORT ============
  useEffect(() => {
    if (typeof window === 'undefined' || !navigator.getGamepads) return;
    
    let gamepadIndex = null;
    let lastButtons = {};
    let animationFrame = null;
    
    const handleGamepadConnected = (event) => {
      gamepadIndex = event.gamepad.index;
      setTvNavMode(true); // Activate TV nav mode when gamepad connected
      console.log('Gamepad connected:', event.gamepad.id);
    };
    
    const handleGamepadDisconnected = () => {
      gamepadIndex = null;
    };
    
    const pollGamepad = () => {
      if (gamepadIndex === null) {
        // Check if any gamepad is connected
        const gamepads = navigator.getGamepads();
        for (let i = 0; i < gamepads.length; i++) {
          if (gamepads[i]) {
            gamepadIndex = i;
            break;
          }
        }
      }
      
      if (gamepadIndex === null) {
        animationFrame = requestAnimationFrame(pollGamepad);
        return;
      }
      
      const gamepad = navigator.getGamepads()[gamepadIndex];
      if (!gamepad) {
        animationFrame = requestAnimationFrame(pollGamepad);
        return;
      }
      
      const buttons = gamepad.buttons;
      const axes = gamepad.axes;
      const threshold = 0.5;
      
      // Check for new button press
      const isNewPress = (buttonIndex) => {
        const pressed = buttons[buttonIndex]?.pressed;
        const wasPressed = lastButtons[buttonIndex];
        lastButtons[buttonIndex] = pressed;
        return pressed && !wasPressed;
      };
      
      // D-pad navigation (standard mapping)
      // 12 = Up, 13 = Down, 14 = Left, 15 = Right
      if (!selectedPortal) {
        if (isNewPress(14) || isNewPress(12)) { // Left or Up
          setTvNavMode(true);
          setTvFocusedPortalIndex(prev => (prev - 1 + PORTAL_DATA.length) % PORTAL_DATA.length);
        }
        if (isNewPress(15) || isNewPress(13)) { // Right or Down
          setTvNavMode(true);
          setTvFocusedPortalIndex(prev => (prev + 1) % PORTAL_DATA.length);
        }
        
        // Left stick navigation
        if (Math.abs(axes[0]) > threshold || Math.abs(axes[1]) > threshold) {
          if (!lastButtons.stickMoved) {
            lastButtons.stickMoved = true;
            setTvNavMode(true);
            if (axes[0] < -threshold || axes[1] < -threshold) {
              setTvFocusedPortalIndex(prev => (prev - 1 + PORTAL_DATA.length) % PORTAL_DATA.length);
            }
            if (axes[0] > threshold || axes[1] > threshold) {
              setTvFocusedPortalIndex(prev => (prev + 1) % PORTAL_DATA.length);
            }
            setTimeout(() => { lastButtons.stickMoved = false; }, 250);
          }
        }
      }
      
      // A button (0) = Select portal
      if (isNewPress(0)) {
        if (selectedPortal) {
          // If portal detail is open, could navigate to it
        } else if (tvNavMode) {
          const portal = PORTAL_DATA[tvFocusedPortalIndex];
          if (portal) {
            handlePortalSelect(portal);
          }
        }
      }
      
      // B button (1) = Back/Close
      if (isNewPress(1)) {
        if (selectedPortal) {
          setSelectedPortal(null);
        } else if (tvNavMode) {
          setTvNavMode(false);
        }
      }
      
      animationFrame = requestAnimationFrame(pollGamepad);
    };
    
    window.addEventListener('gamepadconnected', handleGamepadConnected);
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected);
    animationFrame = requestAnimationFrame(pollGamepad);
    
    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected);
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [selectedPortal, tvNavMode, tvFocusedPortalIndex, playPortalShimmer]);
  
  // Focus and scroll the correct portal button when index changes
  useEffect(() => {
    if (tvNavMode || true) { // Always scroll for touch users too
      const portalBtn = document.querySelector(`[data-portal-index="${tvFocusedPortalIndex}"]`);
      if (portalBtn) {
        portalBtn.focus();
        // Smooth scroll the button into view within its container
        portalBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [tvFocusedPortalIndex, tvNavMode]);



  // Get quests for the selected portal
  const portalQuests = useMemo(() => {
    if (!selectedPortal || !dailyQuests.length) return [];
    return dailyQuests.filter(q => q.quest?.portal_id === selectedPortal.id && !q.is_completed);
  }, [selectedPortal, dailyQuests]);

  // Get learning paths for the selected portal
  const portalPaths = useMemo(() => {
    if (!selectedPortal || !learningPaths.length) return [];
    return learningPaths.filter(p => p.room_id === selectedPortal.id);
  }, [selectedPortal, learningPaths]);

  // Calculate path progress
  const getPathProgress = useCallback((path) => {
    const pathLessons = path.lesson_ids || [];
    const completed = pathLessons.filter(lid => completedLessons.includes(lid)).length;
    return {
      completed,
      total: pathLessons.length,
      percent: pathLessons.length > 0 ? Math.round((completed / pathLessons.length) * 100) : 0,
      isComplete: completed >= pathLessons.length && pathLessons.length > 0
    };
  }, [completedLessons]);

  // Handle mouse move for hover effects
  const handleMouseMove = useCallback((event) => {
    if (!canvasContainerRef.current || !sceneRef.current || !cameraRef.current || isNavigating) return;
    
    const rect = canvasContainerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    const intersects = raycasterRef.current.intersectObjects(portalMeshesRef.current, true);
    
    if (intersects.length > 0) {
      const portalName = intersects[0].object.name;
      if (hoveredPortal !== portalName) {
        // Reset previous hovered portal
        if (hoveredPortal) {
          const prevVisual = portalVisualsRef.current.find(p => p.name === hoveredPortal);
          if (prevVisual) prevVisual.material.emissiveIntensity = 0.6;
        }
        // Highlight new hovered portal
        const visual = portalVisualsRef.current.find(p => p.name === portalName);
        if (visual) visual.material.emissiveIntensity = 1.2;
        setHoveredPortal(portalName);
        if (canvasContainerRef.current) canvasContainerRef.current.style.cursor = 'pointer';
      }
    } else {
      if (hoveredPortal) {
        const prevVisual = portalVisualsRef.current.find(p => p.name === hoveredPortal);
        if (prevVisual) prevVisual.material.emissiveIntensity = 0.6;
        setHoveredPortal(null);
        if (canvasContainerRef.current) canvasContainerRef.current.style.cursor = 'default';
      }
    }
  }, [hoveredPortal, isNavigating]);

  // Handle click for portal selection
  const handleClick = useCallback((event) => {
    if (!canvasContainerRef.current || !sceneRef.current || !cameraRef.current || isNavigating) return;
    
    const rect = canvasContainerRef.current.getBoundingClientRect();
    mouseRef.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouseRef.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    raycasterRef.current.setFromCamera(mouseRef.current, cameraRef.current);
    const intersects = raycasterRef.current.intersectObjects(portalMeshesRef.current, true);
    
    if (intersects.length > 0) {
      const portalName = intersects[0].object.name;
      const portal = PORTAL_DATA.find(p => p.name === portalName);
      if (portal) {
        console.log('🎯 Portal clicked:', portal.name, portal.id);
        handlePortalSelect(portal);
        
        // Track portal selection for analytics
        if (selectedChild && !portal.isDirectNav) {
          analyticsAPI.trackActivity({
            user_id: selectedChild.parent_id,
            child_id: selectedChild.id,
            activity_type: 'portal_select',
            realm: portal.id,
            timestamp: new Date().toISOString()
          }).catch(err => console.log('Analytics tracking failed:', err));
        }
      }
    }
  }, [isNavigating, handlePortalSelect, selectedChild]);

  const closeModal = useCallback(() => {
    setSelectedPortal(null);
    setLessons([]);
  }, []);

  // Handle lesson click - properly unmount canvas before navigation
  const handleLessonClick = useCallback((lesson, questId = null) => {
    console.log('📖 Lesson selected:', lesson.title, lesson.id, questId ? `(Quest: ${questId})` : '');
    
    // Prevent any further interactions
    setIsNavigating(true);
    
    // Cancel animation loop immediately
    if (animationIdRef.current) {
      cancelAnimationFrame(animationIdRef.current);
      animationIdRef.current = null;
    }
    
    // Store quest ID if this is a quest-linked lesson
    if (questId) {
      localStorage.setItem('activeQuestId', questId);
    } else {
      localStorage.removeItem('activeQuestId');
    }
    
    // Close modal
    setSelectedPortal(null);
    setLessons([]);
    
    // Navigate after a brief delay to allow cleanup
    setTimeout(() => {
      console.log('🎮 Navigating to lesson player:', lesson.id);
      navigate(`/lesson-player/${lesson.id}`);
    }, 50);
  }, [navigate]);

  // Handle quest click - find and navigate to a lesson for that quest
  const handleQuestClick = useCallback((quest, questDoc) => {
    console.log('🎯 Quest selected:', quest.title, questDoc.quest_id);
    
    // Find a lesson for this portal
    if (lessons.length > 0) {
      // Pick a random lesson from this portal
      const randomLesson = lessons[Math.floor(Math.random() * lessons.length)];
      handleLessonClick(randomLesson, questDoc.quest_id);
    }
  }, [lessons, handleLessonClick]);

  const getPortalColorHex = useCallback((portal) => {
    return '#' + portal.color.toString(16).padStart(6, '0');
  }, []);

  // Setup scene function - called once in onCreated
  const setupScene = useCallback((state) => {
    // Prevent duplicate initialization
    if (isSceneInitializedRef.current) {
      console.log('⚠️ Scene already initialized, skipping');
      return;
    }
    isSceneInitializedRef.current = true;
    
    console.log('🎮 Setting up temple scene');
    
    // Store refs
    sceneRef.current = state.scene;
    cameraRef.current = state.camera;
    rendererRef.current = state.gl;
    
    // Configure renderer - Darker background for better portal contrast
    state.gl.setClearColor('#020408');  // Even deeper cosmic dark for contrast
    state.camera.lookAt(0, 5, 0);  // Look upward toward the sky
    
    // === COSMIC SKY DOME WITH DEPTH LAYERS ===
    const skyDome = new THREE.Group();
    
    // LAYER 1: Distant Galaxy Background (far back, very large) - Darker for contrast
    const distantGalaxyGeo = new THREE.SphereGeometry(120, 64, 32, 0, Math.PI * 2, 0, Math.PI / 2);
    const distantGalaxyMat = new THREE.MeshBasicMaterial({
      color: 0x080312,  // Darker purple-black
      side: THREE.BackSide
    });
    const distantGalaxy = new THREE.Mesh(distantGalaxyGeo, distantGalaxyMat);
    distantGalaxy.position.y = -5;
    skyDome.add(distantGalaxy);
    
    // Distant spiral galaxy texture effect (multiple overlapping bands)
    const spiralConfigs = [
      { radius: 100, size: 18, color: 0x4C1D95, opacity: 0.20, rotX: Math.PI/2.8, rotZ: 0.2, y: 60 },
      { radius: 95, size: 14, color: 0x6D28D9, opacity: 0.18, rotX: Math.PI/2.6, rotZ: 0.4, y: 55 },
      { radius: 90, size: 10, color: 0x7C3AED, opacity: 0.15, rotX: Math.PI/2.4, rotZ: 0.6, y: 50 },
    ];
    spiralConfigs.forEach(cfg => {
      const spiral = new THREE.Mesh(
        new THREE.TorusGeometry(cfg.radius, cfg.size, 16, 64, Math.PI * 1.5),
        new THREE.MeshBasicMaterial({ color: cfg.color, transparent: true, opacity: cfg.opacity, side: THREE.DoubleSide })
      );
      spiral.rotation.x = cfg.rotX;
      spiral.rotation.z = cfg.rotZ;
      spiral.position.y = cfg.y;
      skyDome.add(spiral);
    });
    
    // LAYER 2: Inner sky dome (mid-distance)
    const skyDomeGeo = new THREE.SphereGeometry(80, 64, 32, 0, Math.PI * 2, 0, Math.PI / 2);
    const skyDomeMat = new THREE.MeshBasicMaterial({
      color: 0x0A0318,
      side: THREE.BackSide,
      transparent: true,
      opacity: 0.7
    });
    const skyDomeMesh = new THREE.Mesh(skyDomeGeo, skyDomeMat);
    skyDomeMesh.position.y = -2;
    skyDome.add(skyDomeMesh);
    
    // LAYER 3: Mid-layer nebula clouds (closer, more vibrant) - ENHANCED VIBRANCY
    const galaxyBandGeo = new THREE.TorusGeometry(55, 12, 16, 64, Math.PI);
    const galaxyBandMat = new THREE.MeshBasicMaterial({
      color: 0x7C3AED,
      transparent: true,
      opacity: 0.45,
      side: THREE.DoubleSide
    });
    const galaxyBand = new THREE.Mesh(galaxyBandGeo, galaxyBandMat);
    galaxyBand.rotation.x = Math.PI / 2.5;
    galaxyBand.rotation.z = Math.PI / 5;
    galaxyBand.position.y = 35;
    skyDome.add(galaxyBand);
    
    // Secondary pink/magenta galaxy band - STRONGER PINK
    const galaxyBand2 = new THREE.Mesh(
      new THREE.TorusGeometry(50, 8, 16, 48, Math.PI * 0.9),
      new THREE.MeshBasicMaterial({
        color: 0xDB2777,
        transparent: true,
        opacity: 0.38,
        side: THREE.DoubleSide
      })
    );
    galaxyBand2.rotation.x = Math.PI / 2.2;
    galaxyBand2.rotation.z = -Math.PI / 3;
    galaxyBand2.position.y = 40;
    skyDome.add(galaxyBand2);
    
    // Golden accent band - RICHER GOLD
    const galaxyBand3 = new THREE.Mesh(
      new THREE.TorusGeometry(45, 5, 12, 40, Math.PI * 0.7),
      new THREE.MeshBasicMaterial({
        color: 0xF59E0B,
        transparent: true,
        opacity: 0.32,
        side: THREE.DoubleSide
      })
    );
    galaxyBand3.rotation.x = Math.PI / 3;
    galaxyBand3.rotation.z = Math.PI / 4;
    galaxyBand3.position.y = 32;
    skyDome.add(galaxyBand3);
    
    // === CENTRAL FOCAL POINT - Celestial Light Beam (ENHANCED GLOW) ===
    const beamGroup = new THREE.Group();
    
    // Inner core beam (bright white/cyan) - MAXIMUM BRIGHTNESS
    const coreBeamGeo = new THREE.CylinderGeometry(0.3, 1.5, 40, 16, 1, true);
    const coreBeamMat = new THREE.MeshBasicMaterial({
      color: 0x67E8F9,
      transparent: true,
      opacity: 0.45,
      side: THREE.DoubleSide
    });
    const coreBeam = new THREE.Mesh(coreBeamGeo, coreBeamMat);
    coreBeam.position.y = 22;
    beamGroup.add(coreBeam);
    
    // Middle glow beam (purple/violet) - STRONGER VIOLET
    const midBeamGeo = new THREE.CylinderGeometry(0.8, 3, 45, 16, 1, true);
    const midBeamMat = new THREE.MeshBasicMaterial({
      color: 0xA78BFA,
      transparent: true,
      opacity: 0.28,
      side: THREE.DoubleSide
    });
    const midBeam = new THREE.Mesh(midBeamGeo, midBeamMat);
    midBeam.position.y = 24;
    beamGroup.add(midBeam);
    
    // Outer soft glow (very wide, subtle) - VISIBLE AURA
    const outerBeamGeo = new THREE.CylinderGeometry(2, 6, 50, 16, 1, true);
    const outerBeamMat = new THREE.MeshBasicMaterial({
      color: 0x8B5CF6,
      transparent: true,
      opacity: 0.18,
      side: THREE.DoubleSide
    });
    const outerBeam = new THREE.Mesh(outerBeamGeo, outerBeamMat);
    outerBeam.position.y = 26;
    beamGroup.add(outerBeam);
    
    // Central platform glow (on the ground) - BRIGHT CENTER
    const platformGlowGeo = new THREE.CircleGeometry(4, 32);
    const platformGlowMat = new THREE.MeshBasicMaterial({
      color: 0x67E8F9,
      transparent: true,
      opacity: 0.40,
      side: THREE.DoubleSide
    });
    const platformGlow = new THREE.Mesh(platformGlowGeo, platformGlowMat);
    platformGlow.rotation.x = -Math.PI / 2;
    platformGlow.position.y = 0.15;
    beamGroup.add(platformGlow);
    
    // Secondary platform rings
    for (let i = 1; i <= 3; i++) {
      const ringGeo = new THREE.RingGeometry(2 + i * 1.5, 2.5 + i * 1.5, 32);
      const ringMat = new THREE.MeshBasicMaterial({
        color: i === 1 ? 0xA78BFA : (i === 2 ? 0x8B5CF6 : 0x6D28D9),
        transparent: true,
        opacity: 0.15 - i * 0.03,
        side: THREE.DoubleSide
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.rotation.x = -Math.PI / 2;
      ring.position.y = 0.1;
      beamGroup.add(ring);
    }
    
    state.scene.add(beamGroup);
    
    // === COSMIC HORIZON GLOW ===
    const horizonGlowGeo = new THREE.TorusGeometry(70, 8, 16, 64);
    const horizonGlowMat = new THREE.MeshBasicMaterial({
      color: 0x6366F1,
      transparent: true,
      opacity: 0.12,
      side: THREE.DoubleSide
    });
    const horizonGlow = new THREE.Mesh(horizonGlowGeo, horizonGlowMat);
    horizonGlow.rotation.x = Math.PI / 2;
    horizonGlow.position.y = 0;
    skyDome.add(horizonGlow);
    
    state.scene.add(skyDome);
    
    // === LIGHTING (Enhanced with dramatic cosmic lighting - MAXIMUM VIBRANCY) ===
    const ambientLight = new THREE.AmbientLight(0x8B5CF6, 1.0);
    state.scene.add(ambientLight);
    
    // Central orb light (bright cyan glow) - MAXIMUM GLOW
    const orbLight = new THREE.PointLight(0x22D3EE, 5.0, 35);
    orbLight.position.set(0, 2.5, 0);
    state.scene.add(orbLight);
    
    // Warm golden accent lights (stronger) - ENHANCED WARMTH
    const goldenLight1 = new THREE.PointLight(0xFCD34D, 2.5, 40);
    goldenLight1.position.set(-12, 10, -12);
    state.scene.add(goldenLight1);
    
    const goldenLight2 = new THREE.PointLight(0xF59E0B, 2.2, 35);
    goldenLight2.position.set(12, 12, 10);
    state.scene.add(goldenLight2);
    
    // Purple/magenta accent lights (stronger) - MAGICAL PURPLE
    const purpleLight = new THREE.PointLight(0xA855F7, 2.0, 38);
    purpleLight.position.set(-10, 15, 12);
    state.scene.add(purpleLight);
    
    const magentaLight = new THREE.PointLight(0xEC4899, 1.8, 35);
    magentaLight.position.set(14, 18, -14);
    state.scene.add(magentaLight);
    
    // Additional rim lights for depth - SKY ILLUMINATION
    const rimLight1 = new THREE.PointLight(0x3B82F6, 1.5, 50);
    rimLight1.position.set(0, 30, -20);
    state.scene.add(rimLight1);
    
    // Secondary rim light for more sky illumination
    const rimLight2 = new THREE.PointLight(0x8B5CF6, 1.2, 40);
    rimLight2.position.set(0, 25, 15);
    state.scene.add(rimLight2);
    
    // Hemisphere light for overall sky color (brighter) - VIBRANT SKY
    const hemiLight = new THREE.HemisphereLight(0xA855F7, 0x1E1B4B, 1.2);
    state.scene.add(hemiLight);
    
    // === STARFIELD (Enhanced with multiple layers for depth) ===
    
    // LAYER 1: Distant background stars (smaller, dimmer)
    const distantStarGeometry = new THREE.BufferGeometry();
    const distantStarCount = 800;
    const distantStarPositions = new Float32Array(distantStarCount * 3);
    
    for (let i = 0; i < distantStarCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const radius = 70 + Math.random() * 30;
      
      distantStarPositions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      distantStarPositions[i * 3 + 1] = Math.abs(radius * Math.cos(phi)) * 0.8 + 10;
      distantStarPositions[i * 3 + 2] = radius * Math.sin(phi) * Math.sin(theta);
    }
    distantStarGeometry.setAttribute('position', new THREE.BufferAttribute(distantStarPositions, 3));
    const distantStarMaterial = new THREE.PointsMaterial({
      color: 0xE2E8F0,
      size: 0.08,
      transparent: true,
      opacity: 0.75,
      sizeAttenuation: true
    });
    const distantStars = new THREE.Points(distantStarGeometry, distantStarMaterial);
    state.scene.add(distantStars);
    
    // LAYER 2: Mid-layer stars (varied colors and brightness) - BRIGHTER
    const starGeometry = new THREE.BufferGeometry();
    const starCount = 800;
    const starPositions = new Float32Array(starCount * 3);
    const starColors = new Float32Array(starCount * 3);
    const starSizes = new Float32Array(starCount);
    const starPhases = new Float32Array(starCount);
    
    // Star color variations
    const starColorOptions = [
      [1.0, 1.0, 1.0],    // White
      [1.0, 0.95, 0.85],  // Warm white
      [0.85, 0.9, 1.0],   // Cool white
      [1.0, 0.85, 0.7],   // Golden
      [0.9, 0.85, 1.0],   // Purple tint
      [0.7, 0.9, 1.0],    // Cyan tint
    ];
    
    for (let i = 0; i < starCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const radius = 25 + Math.random() * 25;
      
      starPositions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      starPositions[i * 3 + 1] = Math.abs(radius * Math.cos(phi)) * 0.6 + 8;
      starPositions[i * 3 + 2] = radius * Math.sin(phi) * Math.sin(theta);
      
      const colorChoice = starColorOptions[Math.floor(Math.random() * starColorOptions.length)];
      starColors[i * 3] = colorChoice[0];
      starColors[i * 3 + 1] = colorChoice[1];
      starColors[i * 3 + 2] = colorChoice[2];
      
      starSizes[i] = 0.05 + Math.random() * 0.12;
      starPhases[i] = Math.random() * Math.PI * 2;
    }
    
    starGeometry.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    starGeometry.setAttribute('color', new THREE.BufferAttribute(starColors, 3));
    const starMaterial = new THREE.PointsMaterial({
      size: 0.20,
      transparent: true,
      opacity: 1.0,
      sizeAttenuation: true,
      vertexColors: true
    });
    const stars = new THREE.Points(starGeometry, starMaterial);
    state.scene.add(stars);
    
    // LAYER 3: Foreground bright stars (closer, larger, twinkling) - MORE VISIBLE
    const brightStarGeometry = new THREE.BufferGeometry();
    const brightStarCount = 80;
    const brightStarPositions = new Float32Array(brightStarCount * 3);
    const brightStarColors = new Float32Array(brightStarCount * 3);
    
    const brightColors = [
      [1.0, 0.95, 0.8],   // Golden
      [0.9, 0.85, 1.0],   // Lavender
      [0.8, 1.0, 1.0],    // Cyan
      [1.0, 0.8, 0.9],    // Pink
    ];
    
    for (let i = 0; i < brightStarCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const radius = 18 + Math.random() * 12;
      
      brightStarPositions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      brightStarPositions[i * 3 + 1] = Math.abs(radius * Math.cos(phi)) * 0.5 + 12;
      brightStarPositions[i * 3 + 2] = radius * Math.sin(phi) * Math.sin(theta);
      
      const colorChoice = brightColors[Math.floor(Math.random() * brightColors.length)];
      brightStarColors[i * 3] = colorChoice[0];
      brightStarColors[i * 3 + 1] = colorChoice[1];
      brightStarColors[i * 3 + 2] = colorChoice[2];
    }
    
    brightStarGeometry.setAttribute('position', new THREE.BufferAttribute(brightStarPositions, 3));
    brightStarGeometry.setAttribute('color', new THREE.BufferAttribute(brightStarColors, 3));
    const brightStarMaterial = new THREE.PointsMaterial({
      size: 0.3,
      transparent: true,
      opacity: 1.0,
      sizeAttenuation: true,
      vertexColors: true
    });
    const brightStars = new THREE.Points(brightStarGeometry, brightStarMaterial);
    state.scene.add(brightStars);
    
    // Store refs for animation
    const starData = { positions: starPositions, phases: starPhases };
    
    // === NEBULA CLOUDS (DRAMATIC - Much higher opacity and closer) ===
    const nebulaGroup = new THREE.Group();
    
    // Create dramatic nebula cloud layers - HIGH IN THE SKY DOME - VIBRANT COLORS
    const nebulaConfigs = [
      // Large dramatic purple nebula (overhead) - INCREASED OPACITY
      { color: 0x7C3AED, position: [0, 35, -15], scale: 28, opacity: 0.45 },
      { color: 0x9333EA, position: [-20, 40, -10], scale: 22, opacity: 0.40 },
      { color: 0x6D28D9, position: [22, 38, -12], scale: 20, opacity: 0.35 },
      // Magenta/pink clouds (sky layer) - BRIGHTER
      { color: 0xDB2777, position: [-15, 32, 8], scale: 18, opacity: 0.35 },
      { color: 0xEC4899, position: [18, 36, 5], scale: 16, opacity: 0.30 },
      { color: 0xF472B6, position: [0, 42, -8], scale: 18, opacity: 0.38 },
      // Golden/amber nebula highlights - MORE PROMINENT
      { color: 0xF59E0B, position: [-10, 28, -18], scale: 14, opacity: 0.35 },
      { color: 0xFBBF24, position: [12, 30, -20], scale: 12, opacity: 0.30 },
      { color: 0xFCD34D, position: [5, 25, -22], scale: 10, opacity: 0.28 },
      // Deep blue cosmic dust (far background) - MORE LAYERS
      { color: 0x3B82F6, position: [0, 45, -25], scale: 35, opacity: 0.35 },
      { color: 0x1D4ED8, position: [-25, 38, -20], scale: 22, opacity: 0.28 },
      { color: 0x2563EB, position: [20, 42, -22], scale: 18, opacity: 0.25 },
      // Teal/cyan accents (horizon and mid-sky)
      { color: 0x14B8A6, position: [25, 20, -30], scale: 16, opacity: 0.25 },
      { color: 0x06B6D4, position: [-22, 18, -28], scale: 14, opacity: 0.22 },
      { color: 0x22D3EE, position: [0, 22, -35], scale: 20, opacity: 0.20 },
      // Additional violet layers for depth
      { color: 0x8B5CF6, position: [-8, 50, -30], scale: 25, opacity: 0.32 },
      { color: 0xA78BFA, position: [15, 48, -28], scale: 20, opacity: 0.28 },
    ];
    
    const nebulaClouds = [];
    nebulaConfigs.forEach((config, index) => {
      const geometry = new THREE.SphereGeometry(config.scale, 32, 32);
      const material = new THREE.MeshBasicMaterial({
        color: config.color,
        transparent: true,
        opacity: config.opacity,
        side: THREE.BackSide,
        depthWrite: false
      });
      const cloud = new THREE.Mesh(geometry, material);
      cloud.position.set(...config.position);
      nebulaGroup.add(cloud);
      nebulaClouds.push({ mesh: cloud, baseOpacity: config.opacity, phase: index * 0.3 });
    });
    state.scene.add(nebulaGroup);
    
    // === GROUND MIST LAYERS (Atmospheric fog at floor level) - MORE PROMINENT ===
    const mistGroup = new THREE.Group();
    
    // Inner mist (close to floor, denser)
    for (let i = 0; i < 12; i++) {
      const mistGeo = new THREE.PlaneGeometry(30 + Math.random() * 20, 5);
      const mistMat = new THREE.MeshBasicMaterial({
        color: i % 3 === 0 ? 0x8B5CF6 : (i % 3 === 1 ? 0x6366F1 : 0xA78BFA),
        transparent: true,
        opacity: 0.12 + Math.random() * 0.08,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const mistPlane = new THREE.Mesh(mistGeo, mistMat);
      mistPlane.position.set(
        (Math.random() - 0.5) * 35,
        0.3 + Math.random() * 2.5,
        (Math.random() - 0.5) * 35
      );
      mistPlane.rotation.x = -Math.PI / 2;
      mistPlane.rotation.z = Math.random() * Math.PI;
      mistGroup.add(mistPlane);
    }
    
    // Outer mystical glow (around edges)
    for (let i = 0; i < 6; i++) {
      const glowGeo = new THREE.CircleGeometry(8 + Math.random() * 4, 32);
      const glowMat = new THREE.MeshBasicMaterial({
        color: 0x7C3AED,
        transparent: true,
        opacity: 0.06,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const glowPlane = new THREE.Mesh(glowGeo, glowMat);
      const angle = (i / 6) * Math.PI * 2;
      glowPlane.position.set(
        Math.cos(angle) * 12,
        0.2,
        Math.sin(angle) * 12
      );
      glowPlane.rotation.x = -Math.PI / 2;
      mistGroup.add(glowPlane);
    }
    state.scene.add(mistGroup);
    
    // === COSMIC CONSTELLATION NETWORKS (HIGH IN THE SKY DOME) - ENHANCED VISIBILITY ===
    const constellationGroup = new THREE.Group();
    const constellationConfigs = [
      // Major constellation - Crown (high center) - LARGER & BRIGHTER
      { 
        points: [[0, 25, -20], [5, 28, -18], [8, 24, -16], [5, 20, -18], [0, 22, -20], [-5, 20, -18], [-8, 24, -16], [-5, 28, -18], [0, 25, -20]], 
        color: 0xFCD34D, 
        starSize: 0.45,
        lineWidth: 3
      },
      // Triangle constellation (left sky)
      { 
        points: [[-20, 22, -15], [-15, 28, -12], [-10, 22, -18], [-20, 22, -15]], 
        color: 0xA78BFA,
        starSize: 0.38,
        lineWidth: 2
      },
      // Diamond constellation (right sky)
      { 
        points: [[18, 24, -16], [22, 30, -12], [26, 24, -10], [22, 18, -14], [18, 24, -16]], 
        color: 0x67E8F9,
        starSize: 0.38,
        lineWidth: 2
      },
      // Arc constellation (very high)
      { 
        points: [[-12, 35, -22], [-6, 38, -24], [0, 40, -25], [6, 38, -24], [12, 35, -22]], 
        color: 0xFB7185,
        starSize: 0.35,
        lineWidth: 2
      },
      // Orion-like pattern (mid sky, left)
      {
        points: [[-25, 18, -25], [-22, 22, -22], [-20, 18, -28], [-22, 14, -26], [-25, 18, -25]],
        color: 0x22D3EE,
        starSize: 0.32,
        lineWidth: 2
      },
      // Small cluster (mid sky, right)
      {
        points: [[15, 16, -28], [18, 20, -25], [22, 16, -30], [18, 12, -28], [15, 16, -28]],
        color: 0xFBBF24,
        starSize: 0.30,
        lineWidth: 2
      }
    ];
    
    const constellationMeshes = [];
    constellationConfigs.forEach((config, cIndex) => {
      // Create glowing line connection between stars
      const lineGeometry = new THREE.BufferGeometry();
      const positions = new Float32Array(config.points.length * 3);
      config.points.forEach((point, i) => {
        positions[i * 3] = point[0];
        positions[i * 3 + 1] = point[1];
        positions[i * 3 + 2] = point[2];
      });
      lineGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      
      // Main visible line
      const lineMaterial = new THREE.LineBasicMaterial({
        color: config.color,
        transparent: true,
        opacity: 0.8,
        linewidth: config.lineWidth
      });
      const line = new THREE.Line(lineGeometry, lineMaterial);
      constellationGroup.add(line);
      
      // Add bright glowing stars at each vertex
      config.points.forEach((point, pIndex) => {
        // Core star (bright white)
        const starGeo = new THREE.SphereGeometry(config.starSize, 16, 16);
        const starMat = new THREE.MeshBasicMaterial({
          color: 0xFFFFFF,
          transparent: true,
          opacity: 1.0
        });
        const star = new THREE.Mesh(starGeo, starMat);
        star.position.set(...point);
        constellationGroup.add(star);
        constellationMeshes.push({ mesh: star, baseScale: config.starSize, phase: cIndex + pIndex * 0.2 });
        
        // Inner glow (colored) - LARGER
        const innerGlowGeo = new THREE.SphereGeometry(config.starSize * 2.2, 12, 12);
        const innerGlowMat = new THREE.MeshBasicMaterial({
          color: config.color,
          transparent: true,
          opacity: 0.8
        });
        const innerGlow = new THREE.Mesh(innerGlowGeo, innerGlowMat);
        innerGlow.position.set(...point);
        constellationGroup.add(innerGlow);
        
        // Outer glow halo (large, soft) - INCREASED SIZE
        const haloGeo = new THREE.SphereGeometry(config.starSize * 4.5, 12, 12);
        const haloMat = new THREE.MeshBasicMaterial({
          color: config.color,
          transparent: true,
          opacity: 0.35
        });
        const halo = new THREE.Mesh(haloGeo, haloMat);
        halo.position.set(...point);
        constellationGroup.add(halo);
      });
    });
    state.scene.add(constellationGroup);
    
    // === FLOATING PARTICLES (Multi-colored magical dust) ===
    const floatingParticleCount = 300; // Increased for richer atmosphere
    const floatingParticleGeometry = new THREE.BufferGeometry();
    const floatingParticlePositions = new Float32Array(floatingParticleCount * 3);
    const floatingParticleColors = new Float32Array(floatingParticleCount * 3);
    const floatingParticleVelocities = [];
    
    // Magical particle colors - aligned with Temple theme
    const particleColorPalette = [
      [0.98, 0.84, 0.45], // Gold (primary)
      [0.45, 0.90, 0.90], // Soft cyan
      [0.70, 0.50, 0.90], // Purple
      [1.0, 1.0, 0.95],   // Faint white/star
      [0.98, 0.78, 0.35], // Warm gold
      [0.50, 0.85, 0.85], // Teal
    ];
    
    for (let i = 0; i < floatingParticleCount; i++) {
      floatingParticlePositions[i * 3] = (Math.random() - 0.5) * 40;
      floatingParticlePositions[i * 3 + 1] = Math.random() * 25;
      floatingParticlePositions[i * 3 + 2] = (Math.random() - 0.5) * 40;
      
      const colorChoice = particleColorPalette[Math.floor(Math.random() * particleColorPalette.length)];
      floatingParticleColors[i * 3] = colorChoice[0];
      floatingParticleColors[i * 3 + 1] = colorChoice[1];
      floatingParticleColors[i * 3 + 2] = colorChoice[2];
      
      floatingParticleVelocities.push({
        x: (Math.random() - 0.5) * 0.005,
        y: 0.003 + Math.random() * 0.005, // Slower, more graceful
        z: (Math.random() - 0.5) * 0.005
      });
    }
    
    floatingParticleGeometry.setAttribute('position', new THREE.BufferAttribute(floatingParticlePositions, 3));
    floatingParticleGeometry.setAttribute('color', new THREE.BufferAttribute(floatingParticleColors, 3));
    const floatingParticleMaterial = new THREE.PointsMaterial({
      size: 0.08, // Slightly larger
      transparent: true,
      opacity: 0.65,
      sizeAttenuation: true,
      vertexColors: true
    });
    const floatingParticles = new THREE.Points(floatingParticleGeometry, floatingParticleMaterial);
    state.scene.add(floatingParticles);
    
    const floatingParticleData = { positions: floatingParticlePositions, velocities: floatingParticleVelocities };
    
    // === SECONDARY LAYER - Slow drifting star motes (near floor) ===
    const starMoteCount = 80;
    const starMoteGeometry = new THREE.BufferGeometry();
    const starMotePositions = new Float32Array(starMoteCount * 3);
    const starMoteVelocities = [];
    
    for (let i = 0; i < starMoteCount; i++) {
      starMotePositions[i * 3] = (Math.random() - 0.5) * 30;
      starMotePositions[i * 3 + 1] = 0.5 + Math.random() * 5; // Near floor level
      starMotePositions[i * 3 + 2] = (Math.random() - 0.5) * 30;
      
      starMoteVelocities.push({
        x: (Math.random() - 0.5) * 0.003,
        y: 0.001 + Math.random() * 0.002, // Very slow rise
        z: (Math.random() - 0.5) * 0.003
      });
    }
    
    starMoteGeometry.setAttribute('position', new THREE.BufferAttribute(starMotePositions, 3));
    const starMoteMaterial = new THREE.PointsMaterial({
      size: 0.12,
      color: 0xFCD34D, // Gold
      transparent: true,
      opacity: 0.5,
      sizeAttenuation: true
    });
    const starMotes = new THREE.Points(starMoteGeometry, starMoteMaterial);
    state.scene.add(starMotes);
    
    const starMoteData = { positions: starMotePositions, velocities: starMoteVelocities };
    
    // === DECORATIVE SHOOTING STARS (Non-interactive, atmospheric) ===
    const shootingStars = [];
    const createShootingStar = () => {
      const startAngle = Math.random() * Math.PI * 2;
      const startRadius = 30 + Math.random() * 10;
      const startY = 15 + Math.random() * 10;
      
      // Create a glowing trail geometry with more points for smoother trail
      const trailLength = 20;
      const geometry = new THREE.BufferGeometry();
      const positions = new Float32Array(trailLength * 3);
      const colors = new Float32Array(trailLength * 3);
      
      const startX = Math.cos(startAngle) * startRadius;
      const startZ = Math.sin(startAngle) * startRadius;
      
      // Initialize all positions at start point
      for (let i = 0; i < trailLength; i++) {
        positions[i * 3] = startX;
        positions[i * 3 + 1] = startY;
        positions[i * 3 + 2] = startZ;
        // Gradient color from white to blue
        const fade = i / trailLength;
        colors[i * 3] = 1 - fade * 0.3;     // R
        colors[i * 3 + 1] = 1 - fade * 0.5; // G
        colors[i * 3 + 2] = 1;               // B
      }
      
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      
      const material = new THREE.LineBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0.9,
        linewidth: 2
      });
      
      const trail = new THREE.Line(geometry, material);
      state.scene.add(trail);
      
      // Add a glowing head point
      const headGeometry = new THREE.SphereGeometry(0.15, 8, 8);
      const headMaterial = new THREE.MeshBasicMaterial({
        color: 0xFFFFFF,
        transparent: true,
        opacity: 1
      });
      const head = new THREE.Mesh(headGeometry, headMaterial);
      head.position.set(startX, startY, startZ);
      state.scene.add(head);
      
      return {
        mesh: trail,
        head,
        geometry,
        material,
        headMaterial,
        headGeometry,
        startX, startY, startZ,
        targetX: -startX * 0.4 + (Math.random() - 0.5) * 10,
        targetY: startY - 15 - Math.random() * 5,
        targetZ: -startZ * 0.4 + (Math.random() - 0.5) * 10,
        progress: 0,
        speed: 0.015 + Math.random() * 0.01,
        trailLength
      };
    };
    
    // Schedule decorative shooting stars every 30-60 seconds
    let nextShootingStarTime = 2 + Math.random() * 3; // Initial quick one for demo
    
    // === FLOATING PLANETS ===
    const planets = [];
    const planetConfigs = [
      { color: 0x8B5CF6, size: 0.5, orbit: 18, height: 10, speed: 0.03 }, // Purple
      { color: 0xEC4899, size: 0.35, orbit: 22, height: 12, speed: 0.02 }, // Pink
      { color: 0x3B82F6, size: 0.45, orbit: 20, height: 8, speed: 0.025 }, // Blue
      { color: 0xF59E0B, size: 0.3, orbit: 16, height: 14, speed: 0.035 }, // Orange
      { color: 0x10B981, size: 0.4, orbit: 24, height: 11, speed: 0.015 } // Teal
    ];
    
    planetConfigs.forEach((config, i) => {
      const planetGeometry = new THREE.SphereGeometry(config.size, 16, 16);
      const planetMaterial = new THREE.MeshStandardMaterial({
        color: config.color,
        emissive: config.color,
        emissiveIntensity: 0.3,
        roughness: 0.7
      });
      const planet = new THREE.Mesh(planetGeometry, planetMaterial);
      
      // Add a subtle ring to some planets
      if (i % 2 === 0) {
        const ringGeometry = new THREE.TorusGeometry(config.size * 1.4, 0.02, 8, 32);
        const ringMaterial = new THREE.MeshBasicMaterial({
          color: config.color,
          transparent: true,
          opacity: 0.4
        });
        const ring = new THREE.Mesh(ringGeometry, ringMaterial);
        ring.rotation.x = Math.PI / 2.5;
        planet.add(ring);
      }
      
      state.scene.add(planet);
      planets.push({ mesh: planet, ...config, angle: (i * Math.PI * 2) / planetConfigs.length });
    });
    
    // === TEMPLE ARCHITECTURE - OBSERVATORY STYLE ===
    // Create a temple floor with circular pattern tiles
    const templeArchGroup = new THREE.Group();
    
    // Main floor - large circular stone platform
    const mainFloorGeo = new THREE.CylinderGeometry(14, 14.5, 0.3, 64);
    const mainFloorMat = new THREE.MeshStandardMaterial({
      color: 0x1E293B,
      roughness: 0.8,
      metalness: 0.1
    });
    const mainFloor = new THREE.Mesh(mainFloorGeo, mainFloorMat);
    mainFloor.position.set(0, -0.15, 0);
    templeArchGroup.add(mainFloor);
    
    // Inner decorative ring on floor
    const floorRingGeo = new THREE.TorusGeometry(10, 0.3, 8, 64);
    const floorRingMat = new THREE.MeshStandardMaterial({
      color: 0x374151,
      emissive: 0x6366F1,
      emissiveIntensity: 0.1,
      roughness: 0.6
    });
    const floorRing = new THREE.Mesh(floorRingGeo, floorRingMat);
    floorRing.rotation.x = -Math.PI / 2;
    floorRing.position.set(0, 0.01, 0);
    templeArchGroup.add(floorRing);
    
    // Floor tile patterns (radiating lines)
    for (let i = 0; i < 12; i++) {
      const angle = (i / 12) * Math.PI * 2;
      const lineGeo = new THREE.BoxGeometry(0.08, 0.05, 12);
      const lineMat = new THREE.MeshStandardMaterial({
        color: 0x475569,
        roughness: 0.7
      });
      const line = new THREE.Mesh(lineGeo, lineMat);
      line.position.set(Math.cos(angle) * 6, 0.03, Math.sin(angle) * 6);
      line.rotation.y = angle;
      templeArchGroup.add(line);
    }
    
    // === TEMPLE COLUMNS (8 around perimeter - further out for clear view) ===
    const columnCount = 8;
    const columnRadius = 16; // Further from center for clear portal view
    const columnHeight = 10;
    
    const columnBaseMat = new THREE.MeshStandardMaterial({
      color: 0x4B5563,
      roughness: 0.5,
      metalness: 0.3
    });
    const columnAccentMat = new THREE.MeshStandardMaterial({
      color: 0xFCD34D,
      emissive: 0xFCD34D,
      emissiveIntensity: 0.4,
      roughness: 0.25,
      metalness: 0.7
    });
    
    for (let i = 0; i < columnCount; i++) {
      const angle = (i / columnCount) * Math.PI * 2;
      const x = Math.cos(angle) * columnRadius;
      const z = Math.sin(angle) * columnRadius;
      
      const columnGroup = new THREE.Group();
      columnGroup.position.set(x, 0, z);
      
      // Column base (wider)
      const baseGeo = new THREE.CylinderGeometry(0.6, 0.8, 0.6, 16);
      const base = new THREE.Mesh(baseGeo, columnBaseMat);
      base.position.y = 0.3;
      columnGroup.add(base);
      
      // Column shaft
      const shaftGeo = new THREE.CylinderGeometry(0.4, 0.5, columnHeight, 16);
      const shaft = new THREE.Mesh(shaftGeo, columnBaseMat);
      shaft.position.y = columnHeight / 2 + 0.6;
      columnGroup.add(shaft);
      
      // Column capital (decorative top)
      const capitalGeo = new THREE.CylinderGeometry(0.7, 0.4, 0.5, 16);
      const capital = new THREE.Mesh(capitalGeo, columnBaseMat);
      capital.position.y = columnHeight + 0.85;
      columnGroup.add(capital);
      
      // Golden accent band on capital
      const bandGeo = new THREE.TorusGeometry(0.55, 0.08, 8, 16);
      const band = new THREE.Mesh(bandGeo, columnAccentMat);
      band.rotation.x = Math.PI / 2;
      band.position.y = columnHeight + 0.7;
      columnGroup.add(band);
      
      // Glowing orb on top of column
      const orbGeo = new THREE.SphereGeometry(0.2, 16, 16);
      const orbMat = new THREE.MeshBasicMaterial({
        color: 0x8B5CF6,
        transparent: true,
        opacity: 0.8
      });
      const columnOrb = new THREE.Mesh(orbGeo, orbMat);
      columnOrb.position.y = columnHeight + 1.3;
      columnGroup.add(columnOrb);
      
      // Orb glow
      const orbGlowGeo = new THREE.SphereGeometry(0.35, 8, 8);
      const orbGlowMat = new THREE.MeshBasicMaterial({
        color: 0x8B5CF6,
        transparent: true,
        opacity: 0.2
      });
      const orbGlow = new THREE.Mesh(orbGlowGeo, orbGlowMat);
      orbGlow.position.y = columnHeight + 1.3;
      columnGroup.add(orbGlow);
      
      templeArchGroup.add(columnGroup);
    }
    
    // === DECORATIVE ARCHES BETWEEN COLUMNS ===
    for (let i = 0; i < columnCount; i++) {
      const angle1 = (i / columnCount) * Math.PI * 2;
      const angle2 = ((i + 1) / columnCount) * Math.PI * 2;
      const midAngle = (angle1 + angle2) / 2;
      
      const x = Math.cos(midAngle) * columnRadius;
      const z = Math.sin(midAngle) * columnRadius;
      
      // Arch top beam
      const archGeo = new THREE.TorusGeometry(2.3, 0.12, 8, 16, Math.PI);
      const archMat = new THREE.MeshStandardMaterial({
        color: 0x374151,
        roughness: 0.6
      });
      const arch = new THREE.Mesh(archGeo, archMat);
      arch.position.set(x, columnHeight + 0.6, z);
      arch.rotation.y = midAngle + Math.PI / 2;
      arch.rotation.x = Math.PI;
      templeArchGroup.add(arch);
    }
    
    // === BALUSTRADE/RAILING AROUND PLATFORM EDGE ===
    const railRadius = 14;
    const railHeight = 1.0;
    
    // Rail posts
    for (let i = 0; i < 24; i++) {
      const angle = (i / 24) * Math.PI * 2;
      const x = Math.cos(angle) * railRadius;
      const z = Math.sin(angle) * railRadius;
      
      // Skip positions near columns
      const nearColumn = [...Array(columnCount)].some((_, ci) => {
        const colAngle = (ci / columnCount) * Math.PI * 2;
        return Math.abs(angle - colAngle) < 0.3 || Math.abs(angle - colAngle + Math.PI * 2) < 0.3;
      });
      if (nearColumn) continue;
      
      const postGeo = new THREE.CylinderGeometry(0.08, 0.1, railHeight, 8);
      const postMat = new THREE.MeshStandardMaterial({ color: 0x475569, roughness: 0.7 });
      const post = new THREE.Mesh(postGeo, postMat);
      post.position.set(x, railHeight / 2, z);
      templeArchGroup.add(post);
      
      // Post cap
      const capGeo = new THREE.SphereGeometry(0.1, 8, 8);
      const cap = new THREE.Mesh(capGeo, columnAccentMat);
      cap.position.set(x, railHeight + 0.05, z);
      templeArchGroup.add(cap);
    }
    
    // Continuous rail ring
    const railRingGeo = new THREE.TorusGeometry(railRadius, 0.06, 8, 64);
    const railRingMat = new THREE.MeshStandardMaterial({ color: 0x64748B, roughness: 0.5 });
    const railRing = new THREE.Mesh(railRingGeo, railRingMat);
    railRing.rotation.x = Math.PI / 2;
    railRing.position.y = railHeight;
    templeArchGroup.add(railRing);
    
    state.scene.add(templeArchGroup);
    
    // === TEMPLE RIM LIGHTING (Golden glow on pillars and arches) ===
    const rimLightGroup = new THREE.Group();
    
    // Add warm golden lights at each column base
    for (let i = 0; i < columnCount; i++) {
      const angle = (i / columnCount) * Math.PI * 2;
      const x = Math.cos(angle) * columnRadius;
      const z = Math.sin(angle) * columnRadius;
      
      // Golden uplighting at column base
      const columnLight = new THREE.PointLight(0xFCD34D, 0.8, 8);
      columnLight.position.set(x, 1.5, z);
      rimLightGroup.add(columnLight);
      
      // Secondary cooler light at column top
      const topLight = new THREE.PointLight(0xA78BFA, 0.4, 6);
      topLight.position.set(x, columnHeight + 1, z);
      rimLightGroup.add(topLight);
    }
    
    // Arch lighting (subtle golden glow)
    for (let i = 0; i < columnCount; i++) {
      const angle1 = (i / columnCount) * Math.PI * 2;
      const angle2 = ((i + 1) / columnCount) * Math.PI * 2;
      const midAngle = (angle1 + angle2) / 2;
      const archX = Math.cos(midAngle) * (columnRadius - 0.5);
      const archZ = Math.sin(midAngle) * (columnRadius - 0.5);
      
      // Soft golden light under each arch
      const archLight = new THREE.PointLight(0xF59E0B, 0.5, 5);
      archLight.position.set(archX, columnHeight + 2, archZ);
      rimLightGroup.add(archLight);
    }
    
    state.scene.add(rimLightGroup);
    
    // === CENTRAL PLATFORM (for Knowledge Orb) ===
    const centralPlatformGeo = new THREE.CylinderGeometry(2, 2.3, 0.5, 32);
    const centralPlatformMat = new THREE.MeshStandardMaterial({
      color: 0x0F172A,  // Darker for better contrast
      roughness: 0.7
    });
    const centralPlatform = new THREE.Mesh(centralPlatformGeo, centralPlatformMat);
    centralPlatform.position.set(0, 0.25, 0);
    state.scene.add(centralPlatform);
    
    // === FLOOR REFLECTIONS - Subtle mirrored portal light ===
    // Create a reflective floor plane that shows subtle portal glow reflections
    const reflectionPlaneGeo = new THREE.PlaneGeometry(35, 35);  // Slightly larger
    const reflectionPlaneMat = new THREE.MeshBasicMaterial({
      color: 0x080D14,  // Darker floor
      transparent: true,
      opacity: 0.4,
      side: THREE.DoubleSide,
      depthWrite: false
    });
    const reflectionPlane = new THREE.Mesh(reflectionPlaneGeo, reflectionPlaneMat);
    reflectionPlane.rotation.x = -Math.PI / 2;
    reflectionPlane.position.y = 0.05;
    state.scene.add(reflectionPlane);
    
    // Create subtle light beam reflections for each portal
    const portalReflections = [];
    PORTAL_DATA.forEach((portal, index) => {
      const [px, py, pz] = portal.position;
      const scaledX = px * 1.3;
      const scaledZ = pz * 1.3;
      
      // Elongated reflection blob pointing toward center
      const reflectionGeo = new THREE.PlaneGeometry(2, 4);
      const reflectionMat = new THREE.MeshBasicMaterial({
        color: portal.color,
        transparent: true,
        opacity: 0.08,
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const reflection = new THREE.Mesh(reflectionGeo, reflectionMat);
      reflection.rotation.x = -Math.PI / 2;
      
      // Position between portal and center
      const midX = scaledX * 0.6;
      const midZ = scaledZ * 0.6;
      reflection.position.set(midX, 0.06, midZ);
      
      // Rotate to point toward center
      const angle = Math.atan2(-midZ, -midX);
      reflection.rotation.z = angle + Math.PI / 2;
      
      state.scene.add(reflection);
      portalReflections.push({ mesh: reflection, baseOpacity: 0.08 });
    });
    
    // Platform accent ring
    const platformRingGeo = new THREE.TorusGeometry(2.15, 0.08, 8, 32);
    const platformRingMat = new THREE.MeshStandardMaterial({
      color: 0x22D3EE,
      emissive: 0x22D3EE,
      emissiveIntensity: 0.4,
      roughness: 0.3
    });
    const platformRing = new THREE.Mesh(platformRingGeo, platformRingMat);
    platformRing.rotation.x = -Math.PI / 2;
    platformRing.position.set(0, 0.51, 0);
    state.scene.add(platformRing);
    
    // === PORTAL PEDESTALS (5 around the central area) ===
    const portalPedestals = [];
    PORTAL_DATA.forEach((portal, index) => {
      const pedestalGroup = new THREE.Group();
      const [px, py, pz] = portal.position;
      
      // Move portals slightly outward for better framing
      const scaledX = px * 1.3;
      const scaledZ = pz * 1.3;
      
      pedestalGroup.position.set(scaledX, 0, scaledZ);
      
      // Pedestal base
      const pedBaseGeo = new THREE.CylinderGeometry(1.0, 1.2, 0.4, 16);
      const pedBaseMat = new THREE.MeshStandardMaterial({
        color: 0x334155,
        roughness: 0.7
      });
      const pedBase = new THREE.Mesh(pedBaseGeo, pedBaseMat);
      pedBase.position.y = 0.2;
      pedestalGroup.add(pedBase);
      
      // Pedestal column
      const pedColGeo = new THREE.CylinderGeometry(0.6, 0.8, 0.8, 16);
      const pedCol = new THREE.Mesh(pedColGeo, pedBaseMat);
      pedCol.position.y = 0.8;
      pedestalGroup.add(pedCol);
      
      // Glowing accent ring on pedestal
      const pedRingGeo = new THREE.TorusGeometry(0.7, 0.05, 8, 32);
      const pedRingMat = new THREE.MeshBasicMaterial({
        color: portal.color,
        transparent: true,
        opacity: 0.6
      });
      const pedRing = new THREE.Mesh(pedRingGeo, pedRingMat);
      pedRing.rotation.x = -Math.PI / 2;
      pedRing.position.y = 1.2;
      pedestalGroup.add(pedRing);
      
      state.scene.add(pedestalGroup);
      portalPedestals.push(pedestalGroup);
    });
    
    // === GLOW RINGS (Multiple) ===
    const glowRings = [];
    
    const glowRingGeometry = new THREE.TorusGeometry(1.6, 0.05, 8, 32);
    const glowRingMaterial = new THREE.MeshBasicMaterial({
      color: 0x22D3EE,
      transparent: true,
      opacity: 0.5
    });
    const glowRing = new THREE.Mesh(glowRingGeometry, glowRingMaterial);
    glowRing.rotation.x = -Math.PI / 2;
    glowRing.position.set(0, 1.18, 0);
    state.scene.add(glowRing);
    glowRings.push(glowRing);
    
    // Second outer ring
    const outerRingGeometry = new THREE.TorusGeometry(2.0, 0.03, 8, 48);
    const outerRingMaterial = new THREE.MeshBasicMaterial({
      color: 0x22D3EE,
      transparent: true,
      opacity: 0.3
    });
    const outerRing = new THREE.Mesh(outerRingGeometry, outerRingMaterial);
    outerRing.rotation.x = -Math.PI / 2;
    outerRing.position.set(0, 1.15, 0);
    state.scene.add(outerRing);
    glowRings.push(outerRing);
    
    // === KNOWLEDGE ORB (Enhanced) ===
    const orbGeometry = new THREE.SphereGeometry(0.6, 32, 32);
    const orbMaterial = new THREE.MeshStandardMaterial({
      color: 0x22D3EE,
      emissive: 0x22D3EE,
      emissiveIntensity: 0.7,
      roughness: 0.2,
      metalness: 0.1
    });
    const orb = new THREE.Mesh(orbGeometry, orbMaterial);
    orb.position.set(0, 2, 0);
    state.scene.add(orb);
    
    // Inner orb glow
    const innerGlowGeometry = new THREE.SphereGeometry(0.65, 24, 24);
    const innerGlowMaterial = new THREE.MeshBasicMaterial({
      color: 0x22D3EE,
      transparent: true,
      opacity: 0.3
    });
    const innerGlow = new THREE.Mesh(innerGlowGeometry, innerGlowMaterial);
    innerGlow.position.set(0, 2, 0);
    state.scene.add(innerGlow);
    
    // Outer orb aura
    const auraGeometry = new THREE.SphereGeometry(0.9, 16, 16);
    const auraMaterial = new THREE.MeshBasicMaterial({
      color: 0x22D3EE,
      transparent: true,
      opacity: 0.1,
      side: THREE.BackSide
    });
    const aura = new THREE.Mesh(auraGeometry, auraMaterial);
    aura.position.set(0, 2, 0);
    state.scene.add(aura);
    
    // === TEMPLE PARTICLES (Light Motes) ===
    const particleCount = 50;
    const particleGeometry = new THREE.BufferGeometry();
    const particlePositions = new Float32Array(particleCount * 3);
    const particleVelocities = new Float32Array(particleCount * 3);
    
    for (let i = 0; i < particleCount; i++) {
      particlePositions[i * 3] = (Math.random() - 0.5) * 12;
      particlePositions[i * 3 + 1] = Math.random() * 4 + 0.5;
      particlePositions[i * 3 + 2] = (Math.random() - 0.5) * 12;
      
      particleVelocities[i * 3] = (Math.random() - 0.5) * 0.01;
      particleVelocities[i * 3 + 1] = 0.005 + Math.random() * 0.01;
      particleVelocities[i * 3 + 2] = (Math.random() - 0.5) * 0.01;
    }
    
    particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
    const particleMaterial = new THREE.PointsMaterial({
      color: 0x88CCFF,
      size: 0.06,
      transparent: true,
      opacity: 0.6
    });
    const particles = new THREE.Points(particleGeometry, particleMaterial);
    state.scene.add(particles);
    
    // === PORTALS (SIMPLIFIED - Clean ethereal design with soft glow) ===
    portalMeshesRef.current = [];
    portalVisualsRef.current = [];
    const portalInnerGlows = [];
    const portalParticleSystems = [];
    const portalFloorGlows = []; // Floor glow pools beneath portals
    
    PORTAL_DATA.forEach((portal, pIndex) => {
      const portalGroup = new THREE.Group();
      const [px, py, pz] = portal.position;
      portalGroup.position.set(px, py, pz);
      
      // Main portal ring (elegant torus) - ENHANCED BRIGHTNESS
      const portalGeometry = new THREE.TorusGeometry(1.3, 0.12, 24, 64);
      const portalMaterial = new THREE.MeshStandardMaterial({
        color: portal.color,
        emissive: portal.color,
        emissiveIntensity: 1.2,  // Increased from 0.8 for stronger glow
        roughness: 0.05,
        metalness: 0.4
      });
      const portalMesh = new THREE.Mesh(portalGeometry, portalMaterial);
      portalMesh.name = portal.name;
      portalGroup.add(portalMesh);
      portalVisualsRef.current.push(portalMesh);
      
      // Enhanced outer glow ring - LARGER AND BRIGHTER
      const outerGlowGeo = new THREE.TorusGeometry(1.6, 0.08, 16, 48);
      const outerGlowMat = new THREE.MeshBasicMaterial({
        color: portal.color,
        transparent: true,
        opacity: 0.45,  // Increased from 0.3
        depthWrite: false
      });
      const outerGlow = new THREE.Mesh(outerGlowGeo, outerGlowMat);
      portalGroup.add(outerGlow);
      
      // Second outer halo ring for stronger glow effect
      const haloGeo = new THREE.TorusGeometry(1.9, 0.04, 16, 48);
      const haloMat = new THREE.MeshBasicMaterial({
        color: portal.color,
        transparent: true,
        opacity: 0.25,
        depthWrite: false
      });
      const haloRing = new THREE.Mesh(haloGeo, haloMat);
      portalGroup.add(haloRing);
      
      // Inner portal surface (swirling energy) - BRIGHTER
      const innerGlowGeo = new THREE.CircleGeometry(1.15, 48);
      const innerGlowMat = new THREE.MeshBasicMaterial({
        color: portal.color,
        transparent: true,
        opacity: 0.45,  // Increased from 0.35
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const innerGlowMesh = new THREE.Mesh(innerGlowGeo, innerGlowMat);
      portalGroup.add(innerGlowMesh);
      
      // Lighter center for depth effect - BRIGHTER
      const centerGlowGeo = new THREE.CircleGeometry(0.7, 32);
      const centerGlowMat = new THREE.MeshBasicMaterial({
        color: 0xFFFFFF,
        transparent: true,
        opacity: 0.25,  // Increased from 0.15
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const centerGlow = new THREE.Mesh(centerGlowGeo, centerGlowMat);
      portalGroup.add(centerGlow);
      
      portalInnerGlows.push({ mesh: innerGlowMesh, outerMesh: outerGlow, haloMesh: haloRing, baseOpacity: 0.45 });
      
      // Simple stone pedestal
      const pedestalGeo = new THREE.CylinderGeometry(0.8, 1.0, 0.4, 16);
      const pedestalMat = new THREE.MeshStandardMaterial({
        color: 0x374151,
        roughness: 0.8
      });
      const pedestal = new THREE.Mesh(pedestalGeo, pedestalMat);
      pedestal.position.y = -1.9;
      portalGroup.add(pedestal);
      
      // Pedestal glow ring
      const pedRingGeo = new THREE.TorusGeometry(0.9, 0.04, 8, 32);
      const pedRingMat = new THREE.MeshBasicMaterial({
        color: portal.color,
        transparent: true,
        opacity: 0.5
      });
      const pedRing = new THREE.Mesh(pedRingGeo, pedRingMat);
      pedRing.rotation.x = -Math.PI / 2;
      pedRing.position.y = -1.7;
      portalGroup.add(pedRing);
      
      // Soft particle cloud around portal (floating motes)
      const moteCount = 12;
      const moteGeo = new THREE.BufferGeometry();
      const motePositions = new Float32Array(moteCount * 3);
      const moteAngles = new Float32Array(moteCount);
      
      for (let i = 0; i < moteCount; i++) {
        const angle = (i / moteCount) * Math.PI * 2;
        motePositions[i * 3] = Math.cos(angle) * 0.8;
        motePositions[i * 3 + 1] = Math.sin(angle) * 0.8;
        motePositions[i * 3 + 2] = 0;
        moteAngles[i] = angle;
      }
      
      moteGeo.setAttribute('position', new THREE.BufferAttribute(motePositions, 3));
      const moteMat = new THREE.PointsMaterial({
        color: portal.color,
        size: 0.12,
        transparent: true,
        opacity: 0.7
      });
      const motes = new THREE.Points(moteGeo, moteMat);
      portalGroup.add(motes);
      
      portalParticleSystems.push({
        mesh: motes,
        positions: motePositions,
        angles: moteAngles,
        portalPos: portal.position
      });
      
      // Soft point light for portal glow - MAXIMUM INTENSITY
      const portalLight = new THREE.PointLight(portal.color, 3.5, 12);  // Increased significantly
      portalLight.position.set(0, 0, 0.5);
      portalGroup.add(portalLight);
      
      // Additional glow light - BRIGHT AURA
      const glowLight = new THREE.PointLight(portal.color, 1.8, 8);  // Increased
      glowLight.position.set(0, 0, 1);
      portalGroup.add(glowLight);
      
      // === FLOOR GLOW POOL - Colored light pool beneath portal - VIVID ===
      // Main glow circle on the floor
      const floorGlowGeo = new THREE.CircleGeometry(3.5, 32);  // Even larger radius
      const floorGlowMat = new THREE.MeshBasicMaterial({
        color: portal.color,
        transparent: true,
        opacity: 0.35,  // Much more visible
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const floorGlow = new THREE.Mesh(floorGlowGeo, floorGlowMat);
      floorGlow.rotation.x = -Math.PI / 2;
      floorGlow.position.set(px, 0.12, pz); // Slightly above floor
      floorGlow.name = `floorGlow_${portal.id}`;
      state.scene.add(floorGlow);
      
      // Outer gradient ring for softer falloff - LARGER
      const floorGlowOuterGeo = new THREE.RingGeometry(3.0, 5, 32);  // Larger
      const floorGlowOuterMat = new THREE.MeshBasicMaterial({
        color: portal.color,
        transparent: true,
        opacity: 0.12,  // Increased from 0.08
        side: THREE.DoubleSide,
        depthWrite: false
      });
      const floorGlowOuter = new THREE.Mesh(floorGlowOuterGeo, floorGlowOuterMat);
      floorGlowOuter.rotation.x = -Math.PI / 2;
      floorGlowOuter.position.set(px, 0.11, pz);
      floorGlowOuter.name = `floorGlowOuter_${portal.id}`;
      state.scene.add(floorGlowOuter);
      
      // Store floor glow refs for animation
      portalFloorGlows.push({ 
        inner: floorGlow, 
        outer: floorGlowOuter, 
        color: portal.color,
        baseOpacity: 0.22
      });
      
      state.scene.add(portalGroup);
      
      // Hitbox for interaction - larger hitbox at portal visual position
      const hitboxGeometry = new THREE.SphereGeometry(2.5, 8, 8);
      const hitboxMaterial = new THREE.MeshBasicMaterial({ visible: false });
      const hitbox = new THREE.Mesh(hitboxGeometry, hitboxMaterial);
      hitbox.position.set(px, py, pz);
      hitbox.name = portal.name;
      state.scene.add(hitbox);
      portalMeshesRef.current.push(hitbox);
    });
    
    // === ANIMATION LOOP (Enhanced) ===
    let lastTime = 0;
    const fps = 30;
    const frameInterval = 1000 / fps;
    
    const animate = (currentTime) => {
      if (isUnmountedRef.current) return;
      
      animationIdRef.current = requestAnimationFrame(animate);
      
      const elapsed = currentTime - lastTime;
      if (elapsed < frameInterval) return;
      lastTime = currentTime - (elapsed % frameInterval);
      
      const time = currentTime * 0.001;
      
      // === ENHANCED STAR TWINKLING ===
      // Slowly rotate all star layers at different rates
      stars.rotation.y += 0.0003;
      distantStars.rotation.y += 0.0001;
      brightStars.rotation.y += 0.0004;
      
      // Individual star twinkling effect via size variation
      const starSizeArray = starGeometry.attributes.position.array;
      for (let i = 0; i < starCount; i++) {
        const twinkle = Math.sin(time * 2 + starPhases[i] * 10) * 0.5 + 0.5;
        // Subtle vertical oscillation for shimmer effect
        const origY = starPositions[i * 3 + 1];
        starSizeArray[i * 3 + 1] = origY + Math.sin(time * 1.5 + starPhases[i] * 5) * 0.02;
      }
      starGeometry.attributes.position.needsUpdate = true;
      starMaterial.opacity = 0.75 + Math.sin(time * 0.7) * 0.15;
      
      // Bright stars pulsing
      brightStarMaterial.size = 0.25 + Math.sin(time * 1.2) * 0.08;
      brightStarMaterial.opacity = 0.9 + Math.sin(time * 0.9) * 0.1;
      
      // Distant stars gentle shimmer
      distantStarMaterial.opacity = 0.4 + Math.sin(time * 0.4) * 0.15;
      
      // === DRIFTING NEBULA CLOUDS ===
      nebulaClouds.forEach((cloud, i) => {
        // Pulsing opacity for breathing effect
        cloud.mesh.material.opacity = cloud.baseOpacity * (0.7 + 0.5 * Math.sin(time * 0.25 + cloud.phase));
        
        // Slow drifting motion - each nebula moves in unique patterns
        const driftSpeed = 0.15;
        const driftX = Math.sin(time * driftSpeed + cloud.phase * 2) * 0.8;
        const driftY = Math.cos(time * driftSpeed * 0.7 + cloud.phase) * 0.4;
        const driftZ = Math.sin(time * driftSpeed * 0.5 + cloud.phase * 1.5) * 0.6;
        
        cloud.mesh.position.x += driftX * 0.003;
        cloud.mesh.position.y += driftY * 0.002;
        cloud.mesh.position.z += driftZ * 0.002;
        
        // Slow rotation for swirling effect
        cloud.mesh.rotation.y += 0.0002 * (i % 2 === 0 ? 1 : -1);
        cloud.mesh.rotation.x += 0.00008 * (i % 3 === 0 ? 1 : -1);
        
        // Scale breathing
        const scaleBreath = 1 + Math.sin(time * 0.2 + cloud.phase) * 0.03;
        cloud.mesh.scale.setScalar(scaleBreath);
      });
      
      // === CONSTELLATION STARS TWINKLING ===
      constellationMeshes.forEach((star, i) => {
        const pulse = Math.sin(time * 2 + star.phase * 3) * 0.5 + 0.5;
        const scale = star.baseScale * (0.85 + pulse * 0.4);
        star.mesh.scale.setScalar(scale);
      });
      
      // === CENTRAL LIGHT BEAM SHIMMER ===
      coreBeam.material.opacity = 0.2 + Math.sin(time * 1.5) * 0.08;
      midBeam.material.opacity = 0.12 + Math.sin(time * 1.2 + 0.5) * 0.05;
      outerBeam.material.opacity = 0.06 + Math.sin(time * 0.9 + 1) * 0.03;
      
      // Beam vertical shimmer
      const beamShimmer = Math.sin(time * 2) * 0.1;
      coreBeam.scale.y = 1 + beamShimmer * 0.05;
      midBeam.scale.y = 1 + beamShimmer * 0.03;
      
      // Platform glow pulse
      platformGlow.material.opacity = 0.2 + Math.sin(time * 0.8) * 0.1;
      
      // === GALAXY BANDS ROTATION ===
      galaxyBand.rotation.z += 0.0002;
      galaxyBand2.rotation.z -= 0.00015;
      galaxyBand3.rotation.z += 0.00025;
      
      // Horizon glow pulse
      horizonGlow.material.opacity = 0.1 + Math.sin(time * 0.5) * 0.04;
      
      // === PARALLAX DEPTH EFFECT ===
      // Get mouse position for parallax (-1 to 1 range)
      const mouseX = mouseRef.current.x;
      const mouseY = mouseRef.current.y;
      
      // Apply parallax to different layers at different speeds
      // Far stars move slowest (creates depth illusion)
      distantStars.position.x = mouseX * 0.5;
      distantStars.position.y = mouseY * 0.3;
      
      // Main stars move at medium speed
      stars.position.x = mouseX * 1.2;
      stars.position.y = mouseY * 0.8;
      
      // Bright stars move fastest (closer to viewer)
      brightStars.position.x = mouseX * 2;
      brightStars.position.y = mouseY * 1.2;
      
      // Nebula clouds have subtle parallax
      nebulaClouds.forEach((cloud, i) => {
        const depthFactor = 0.3 + (i % 3) * 0.2;
        cloud.mesh.position.x += mouseX * depthFactor * 0.01;
        cloud.mesh.position.y += mouseY * depthFactor * 0.01;
      });
      
      // Galaxy bands very subtle parallax (far away)
      galaxyBand.position.x = mouseX * 0.3;
      galaxyBand2.position.x = mouseX * 0.4;
      galaxyBand3.position.x = mouseX * 0.35;
      
      // Animate floating particles
      const fPositions = floatingParticleData.positions;
      const fVelocities = floatingParticleData.velocities;
      for (let i = 0; i < floatingParticleCount; i++) {
        fPositions[i * 3] += fVelocities[i].x;
        fPositions[i * 3 + 1] += fVelocities[i].y;
        fPositions[i * 3 + 2] += fVelocities[i].z;
        
        // Reset particles that float too high or far
        if (fPositions[i * 3 + 1] > 22 || Math.abs(fPositions[i * 3]) > 25 || Math.abs(fPositions[i * 3 + 2]) > 25) {
          fPositions[i * 3] = (Math.random() - 0.5) * 35;
          fPositions[i * 3 + 1] = -3;
          fPositions[i * 3 + 2] = (Math.random() - 0.5) * 35;
        }
      }
      floatingParticles.geometry.attributes.position.needsUpdate = true;
      floatingParticleMaterial.opacity = 0.5 + Math.sin(time * 0.8) * 0.2;
      
      // Animate star motes (slow drifting near floor)
      const smPositions = starMoteData.positions;
      const smVelocities = starMoteData.velocities;
      for (let i = 0; i < starMoteCount; i++) {
        smPositions[i * 3] += smVelocities[i].x;
        smPositions[i * 3 + 1] += smVelocities[i].y;
        smPositions[i * 3 + 2] += smVelocities[i].z;
        
        // Reset motes that rise too high or drift too far
        if (smPositions[i * 3 + 1] > 8 || Math.abs(smPositions[i * 3]) > 18 || Math.abs(smPositions[i * 3 + 2]) > 18) {
          smPositions[i * 3] = (Math.random() - 0.5) * 30;
          smPositions[i * 3 + 1] = 0.3;
          smPositions[i * 3 + 2] = (Math.random() - 0.5) * 30;
        }
      }
      starMotes.geometry.attributes.position.needsUpdate = true;
      starMoteMaterial.opacity = 0.4 + Math.sin(time * 0.6) * 0.15;
      
      // Decorative Shooting stars (atmospheric, non-interactive)
      nextShootingStarTime -= 0.033;
      if (nextShootingStarTime <= 0 && shootingStars.length < 2) {
        shootingStars.push(createShootingStar());
        nextShootingStarTime = 30 + Math.random() * 30; // 30-60 seconds between shooting stars
      }
      
      // Update shooting stars
      for (let i = shootingStars.length - 1; i >= 0; i--) {
        const ss = shootingStars[i];
        ss.progress += ss.speed;
        
        const positions = ss.geometry.attributes.position.array;
        
        // Update trail positions (shift down)
        for (let j = ss.trailLength - 1; j > 0; j--) {
          positions[j * 3] = positions[(j - 1) * 3];
          positions[j * 3 + 1] = positions[(j - 1) * 3 + 1];
          positions[j * 3 + 2] = positions[(j - 1) * 3 + 2];
        }
        
        // Calculate current head position
        const currentX = ss.startX + (ss.targetX - ss.startX) * ss.progress;
        const currentY = ss.startY + (ss.targetY - ss.startY) * ss.progress;
        const currentZ = ss.startZ + (ss.targetZ - ss.startZ) * ss.progress;
        
        // Update head position in trail
        positions[0] = currentX;
        positions[1] = currentY;
        positions[2] = currentZ;
        
        // Update glowing head mesh
        ss.head.position.set(currentX, currentY, currentZ);
        
        ss.geometry.attributes.position.needsUpdate = true;
        
        // Fade out as it progresses
        const fadeOut = Math.max(0, 1 - ss.progress * 1.2);
        ss.material.opacity = fadeOut * 0.9;
        ss.headMaterial.opacity = fadeOut;
        ss.head.scale.setScalar(fadeOut);
        
        // Remove completed shooting stars
        if (ss.progress >= 1) {
          state.scene.remove(ss.mesh);
          state.scene.remove(ss.head);
          ss.geometry.dispose();
          ss.material.dispose();
          ss.headGeometry.dispose();
          ss.headMaterial.dispose();
          shootingStars.splice(i, 1);
        }
      }
      
      // Animate planets
      planets.forEach((planet) => {
        planet.angle += planet.speed * 0.033;
        planet.mesh.position.x = Math.cos(planet.angle) * planet.orbit;
        planet.mesh.position.y = planet.height + Math.sin(time * 0.5 + planet.angle) * 0.5;
        planet.mesh.position.z = Math.sin(planet.angle) * planet.orbit;
        planet.mesh.rotation.y += 0.01;
      });
      
      // Animate orb (breathing glow + floating)
      const breathe = Math.sin(time * 0.8);
      orb.position.y = 2 + Math.sin(time * 0.4) * 0.1;
      orb.scale.setScalar(1 + breathe * 0.05);
      orbMaterial.emissiveIntensity = 0.5 + breathe * 0.3;
      
      innerGlow.position.y = orb.position.y;
      innerGlow.scale.setScalar(1 + breathe * 0.08);
      innerGlowMaterial.opacity = 0.2 + breathe * 0.15;
      
      aura.position.y = orb.position.y;
      aura.scale.setScalar(1 + Math.sin(time * 1.2) * 0.1);
      auraMaterial.opacity = 0.08 + Math.sin(time * 1.5) * 0.05;
      
      // Animate glow rings (counter-rotation)
      glowRings[0].rotation.z = time * 0.15;
      glowRings[1].rotation.z = -time * 0.1;
      glowRings[1].scale.setScalar(1 + Math.sin(time * 0.6) * 0.05);
      
      // Animate temple particles (light motes)
      const pPositions = particleGeometry.attributes.position.array;
      for (let i = 0; i < particleCount; i++) {
        pPositions[i * 3] += particleVelocities[i * 3];
        pPositions[i * 3 + 1] += particleVelocities[i * 3 + 1];
        pPositions[i * 3 + 2] += particleVelocities[i * 3 + 2];
        
        // Reset particles that float too high
        if (pPositions[i * 3 + 1] > 5) {
          pPositions[i * 3] = (Math.random() - 0.5) * 12;
          pPositions[i * 3 + 1] = 0.5;
          pPositions[i * 3 + 2] = (Math.random() - 0.5) * 12;
        }
      }
      particleGeometry.attributes.position.needsUpdate = true;
      
      // Animate portals (ENHANCED - pronounced glow pulse for magical feel)
      portalVisualsRef.current.forEach((portal, index) => {
        // Very slow, smooth rotation
        portal.rotation.z = time * 0.08;
        
        // Enhanced breathing glow with more variation
        const breathPhase = Math.sin(time * 0.5 + index * 0.8);
        const pulsePhase = Math.sin(time * 1.2 + index * 1.5) * 0.1;
        portal.material.emissiveIntensity = 0.7 + breathPhase * 0.25 + pulsePhase;
        
        // Scale pulse for "alive" feeling
        const scalePulse = 1 + Math.sin(time * 0.3 + index * 0.5) * 0.02;
        portal.scale.setScalar(scalePulse);
      });
      
      // Animate portal inner glows (ENHANCED - more pronounced swirl and glow)
      portalInnerGlows.forEach((glow, index) => {
        // Smooth inner swirl
        glow.mesh.rotation.z = time * 0.15;
        // Enhanced opacity pulse - BRIGHTER
        const glowPulse = 0.85 + Math.sin(time * 0.6 + index * 0.6) * 0.25;
        glow.mesh.material.opacity = glow.baseOpacity * glowPulse;
        if (glow.outerMesh) {
          glow.outerMesh.rotation.z = -time * 0.1;
          glow.outerMesh.material.opacity = 0.35 + Math.sin(time * 0.7 + index) * 0.15;  // Brighter
        }
        // Animate halo ring if present
        if (glow.haloMesh) {
          glow.haloMesh.rotation.z = time * 0.08;
          glow.haloMesh.material.opacity = 0.18 + Math.sin(time * 0.5 + index * 0.8) * 0.1;
        }
      });
      
      // Animate portal particles (SMOOTH - gentle drift, not rigid)
      portalParticleSystems.forEach((ps, index) => {
        const positions = ps.positions;
        const wispCount = positions.length / 3;
        for (let i = 0; i < wispCount; i++) {
          // Slow, organic movement
          const baseAngle = ps.angles[i];
          const driftAngle = baseAngle + time * 0.3 + Math.sin(time * 0.2 + i) * 0.5;
          const radius = 0.6 + Math.sin(time * 0.4 + i * 0.3) * 0.15;
          const verticalDrift = Math.sin(time * 0.5 + i * 0.4) * 0.2;
          
          positions[i * 3] = Math.cos(driftAngle) * radius;
          positions[i * 3 + 1] = Math.sin(driftAngle) * radius * 0.5 + verticalDrift;
          positions[i * 3 + 2] = Math.sin(driftAngle * 0.3) * 0.15;
        }
        ps.mesh.geometry.attributes.position.needsUpdate = true;
        // Gentle particle opacity pulse
        ps.mesh.material.opacity = 0.6 + Math.sin(time * 0.5 + index * 0.5) * 0.2;
      });
      
      // === ANIMATE PORTAL FLOOR GLOWS ===
      portalFloorGlows.forEach((fg, index) => {
        // Slow pulsing glow on the floor
        const glowPulse = Math.sin(time * 0.4 + index * 0.7) * 0.5 + 0.5;
        fg.inner.material.opacity = fg.baseOpacity * (0.7 + glowPulse * 0.5);
        fg.outer.material.opacity = 0.05 + glowPulse * 0.05;
        
        // Subtle scale breathing
        const scaleFactor = 1 + Math.sin(time * 0.3 + index * 0.5) * 0.03;
        fg.inner.scale.setScalar(scaleFactor);
        fg.outer.scale.setScalar(scaleFactor * 0.98);
      });
      
      // === ANIMATE PORTAL REFLECTIONS ===
      portalReflections.forEach((pr, index) => {
        // Subtle shimmer effect for reflections
        const shimmer = Math.sin(time * 0.5 + index * 0.8) * 0.5 + 0.5;
        pr.mesh.material.opacity = pr.baseOpacity * (0.6 + shimmer * 0.6);
      });
      
      // Animate orb light
      orbLight.intensity = 1.0 + breathe * 0.4;
    };
    
    animationIdRef.current = requestAnimationFrame(animate);
    
    console.log('🎮 Temple scene ready with enhanced effects');
  }, []);

  // Don't render canvas if navigating
  if (isNavigating) {
    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        background: '#050A15',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div className="text-cyan-400 text-lg">Loading lesson...</div>
      </div>
    );
  }

  return (
    <div style={{
      width: '100vw',
      height: '100vh',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Canvas Background Layer */}
      <div 
        ref={canvasContainerRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 0
        }}
        onMouseMove={handleMouseMove}
        onClick={handleClick}
      >
        <Canvas 
          key={canvasKey}
          style={{ width: '100%', height: '100%' }}
          camera={{ position: [0, 1.8, 14], fov: 65 }}
          dpr={1}
          frameloop="demand"
          onCreated={setupScene}
        >
          <OrbitControls 
            enablePan={false}
            enableZoom={true}
            minDistance={8}
            maxDistance={22}
            minPolarAngle={Math.PI / 4}
            maxPolarAngle={Math.PI / 2}
            target={[0, 4, 0]}
          />
        </Canvas>
        
        {/* Cinematic Color Grading Overlay */}
        <div 
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'radial-gradient(ellipse at center, transparent 0%, rgba(10, 5, 25, 0.3) 70%, rgba(5, 0, 15, 0.5) 100%)',
            mixBlendMode: 'multiply'
          }}
        />
        {/* Subtle warm/purple tint for atmosphere */}
        <div 
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, transparent 50%, rgba(236, 72, 153, 0.04) 100%)',
            mixBlendMode: 'screen'
          }}
        />
        {/* Subtle vignette for depth */}
        <div 
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            boxShadow: 'inset 0 0 150px rgba(0, 0, 0, 0.4), inset 0 0 50px rgba(0, 0, 0, 0.3)'
          }}
        />
      </div>

      {/* HTML Overlay */}
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 20,
        pointerEvents: 'none'
      }}>
        {/* Exit Button - Higher z-index on mobile, pushed down to avoid banner */}
        <button
          data-testid="exit-temple-btn"
          onClick={() => navigate('/child-select')}
          style={{ pointerEvents: 'auto' }}
          className="absolute top-16 sm:top-6 left-4 sm:left-6 z-30 text-slate-400 hover:text-white transition-colors flex items-center gap-2 glass-panel px-3 sm:px-4 py-2 rounded-full"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm sm:text-base">Exit Temple</span>
        </button>

        {/* Sound Control */}
        <div 
          style={{ pointerEvents: 'auto' }}
          className="absolute bottom-6 left-6 glass-panel px-4 py-2 rounded-full flex items-center gap-3"
        >
          <button
            onClick={toggleSound}
            className={`p-1.5 rounded-full transition-all ${soundEnabled ? 'text-cyan-400 hover:text-cyan-300' : 'text-slate-500 hover:text-slate-400'}`}
            data-testid="sound-toggle"
            title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          {soundEnabled && (
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={soundVolume}
              onChange={(e) => updateVolume(parseFloat(e.target.value))}
              className="w-20 accent-cyan-500"
              data-testid="sound-volume"
            />
          )}
        </div>

        {/* Child Badge with Sparks and Rank - Mobile: top center, Desktop: top right */}
        {selectedChild && (
          <div 
            style={{ pointerEvents: 'auto' }}
            className="absolute top-3 sm:top-6 left-1/2 sm:left-auto sm:right-6 -translate-x-1/2 sm:translate-x-0 z-25 glass-panel px-3 sm:px-4 py-1.5 sm:py-2 rounded-full flex items-center gap-2 sm:gap-3 max-w-[90%] sm:max-w-none"
          >
            {/* Full Body Avatar - Medium size for visibility */}
            {selectedChild.avatar && Object.keys(selectedChild.avatar).length > 0 ? (
              <div className="relative" style={{ width: 40, height: 60 }}>
                <FullBodyAvatarPreview 
                  avatar={selectedChild.avatar} 
                  size="sm" 
                  type="explorer"
                  isIdle={true}
                  showAura={rank?.current_rank?.threshold >= 150}
                  auraColor={rank?.current_rank?.color || '#818CF8'}
                />
              </div>
            ) : (
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white"
                style={{ backgroundColor: selectedChild.avatar_color || '#818CF8' }}
              >
                <User className="w-4 h-4" />
              </div>
            )}
            <span className="text-white font-semibold">{selectedChild.name}</span>
            {selectedChild.life_path && (
              <span className="text-cyan-400 text-xs">Path {selectedChild.life_path}</span>
            )}
            {/* Rank Badge */}
            {rank?.current_rank && (
              <div 
                className="flex items-center gap-1 text-xs border-l border-white/20 pl-3"
                style={{ color: rank.current_rank.color }}
                data-testid="rank-badge"
              >
                {(() => {
                  const RankIcon = RANK_ICONS[rank.current_rank.icon] || Sparkles;
                  return <RankIcon className="w-3.5 h-3.5" />;
                })()}
                <span className="font-medium">{rank.current_rank.name}</span>
              </div>
            )}
            {/* Total Sparks */}
            <div className="flex items-center gap-1 text-yellow-400 text-sm border-l border-white/20 pl-3">
              <Sparkles className="w-4 h-4" />
              <span>{Object.values(sparks).reduce((a, b) => a + b, 0)}</span>
            </div>
          </div>
        )}

        {/* Life Path Guidance Message - Adjusted for mobile header space */}
        {guidanceMessage && !selectedPortal && (
          <div className="absolute top-1/2 sm:top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 mt-8 sm:-mt-20 px-4 sm:px-0">
            <div className="glass-panel px-4 sm:px-6 py-3 rounded-2xl border border-cyan-500/30 max-w-[90vw] sm:max-w-none">
              <p className="text-cyan-300 text-center text-sm sm:text-base">
                {guidanceMessage}
              </p>
            </div>
          </div>
        )}

        {/* Hover indicator */}
        {hoveredPortal && !selectedPortal && (
          <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2">
            <div className="glass-panel px-4 py-2 rounded-full border border-white/20">
              <p className="text-white text-lg font-semibold">{hoveredPortal}</p>
            </div>
          </div>
        )}

        {/* Status Text */}
        <div className="absolute bottom-20 sm:bottom-4 left-4">
          <p className="text-cyan-400/70 text-sm hidden sm:block">Click a portal to begin</p>
        </div>

        {/* TV/Remote Portal Navigation - Shows when using keyboard/remote */}
        {(tvNavMode || true) && !selectedPortal && (
          <div 
            ref={portalNavRef}
            className="absolute bottom-28 sm:bottom-20 left-0 right-0 sm:left-1/2 sm:-translate-x-1/2 sm:right-auto z-30 px-2 sm:px-0"
            style={{ pointerEvents: 'auto' }}
          >
            <div className="glass-panel px-3 sm:px-4 py-3 rounded-2xl border border-slate-600/50 bg-slate-900/90 backdrop-blur-lg max-w-full">
              <p className="text-center text-slate-400 text-xs mb-3">
                {isTouchDevice 
                  ? 'Tap a portal to explore' 
                  : tvNavMode 
                    ? '← → to select • A or Enter to open' 
                    : 'Tap, use arrows, or gamepad to navigate'
                }
              </p>
              <div className="flex items-center gap-2">
                {/* Left arrow indicator - larger on touch */}
                <button
                  onClick={() => setTvFocusedPortalIndex(prev => (prev - 1 + PORTAL_DATA.length) % PORTAL_DATA.length)}
                  className={`flex-shrink-0 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors active:scale-95 ${
                    isTouchDevice ? 'p-3' : 'p-2'
                  }`}
                  aria-label="Previous portal"
                >
                  <ChevronLeft className={isTouchDevice ? "w-6 h-6" : "w-5 h-5"} />
                </button>
                
                {/* Portal buttons - scrollable on mobile */}
                <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 -mb-1 snap-x snap-mandatory"
                     style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  {PORTAL_DATA.map((portal, index) => {
                    const Icon = portal.icon;
                    const isFocused = tvFocusedPortalIndex === index;
                    const portalColor = '#' + portal.color.toString(16).padStart(6, '0');
                    
                    return (
                      <button
                        key={portal.id}
                        onClick={() => handlePortalSelect(portal)}
                        onFocus={() => setTvFocusedPortalIndex(index)}
                        className={`flex-shrink-0 snap-center flex flex-col items-center gap-1 rounded-xl transition-all tv-portal active:scale-95 ${
                          isTouchDevice ? 'px-3 py-2' : 'px-3 py-2'
                        } ${
                          isFocused 
                            ? 'ring-2 scale-110 bg-slate-800' 
                            : 'bg-slate-800/50 hover:bg-slate-700/50'
                        }`}
                        style={isFocused ? { 
                          ringColor: portalColor,
                          boxShadow: `0 0 20px ${portalColor}60`
                        } : {}}
                        data-portal-nav
                        data-portal-index={index}
                        data-testid={`portal-nav-${portal.id}`}
                        tabIndex={0}
                      >
                        <div 
                          className={`rounded-full flex items-center justify-center ${
                            isTouchDevice ? 'w-12 h-12' : 'w-10 h-10'
                          }`}
                          style={{ 
                            backgroundColor: `${portalColor}30`,
                            border: `2px solid ${portalColor}`
                          }}
                        >
                          <Icon className={isTouchDevice ? "w-6 h-6" : "w-5 h-5"} style={{ color: portalColor }} />
                        </div>
                        <span className={`text-white font-medium text-center leading-tight max-w-[60px] ${
                          isTouchDevice ? 'text-xs' : 'text-[10px]'
                        }`}>
                          {portal.name.split(' ')[0]}
                        </span>
                      </button>
                    );
                  })}
                </div>
                
                {/* Right arrow indicator - larger on touch */}
                <button
                  onClick={() => setTvFocusedPortalIndex(prev => (prev + 1) % PORTAL_DATA.length)}
                  className={`flex-shrink-0 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 text-slate-400 hover:text-white transition-colors active:scale-95 ${
                    isTouchDevice ? 'p-3' : 'p-2'
                  }`}
                  aria-label="Next portal"
                >
                  <ChevronRight className={isTouchDevice ? "w-6 h-6" : "w-5 h-5"} />
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Temple Sky - Prominent Floating Button - Mobile responsive */}
        <button
          onClick={() => {
            setIsNavigating(true);
            if (animationIdRef.current) {
              cancelAnimationFrame(animationIdRef.current);
              animationIdRef.current = null;
            }
            setTimeout(() => navigate('/temple-sky'), 50);
          }}
          className="absolute top-16 sm:top-24 right-4 sm:right-6 group z-20"
          style={{ pointerEvents: 'auto' }}
          data-testid="temple-sky-btn"
          title="View your constellations in Temple Sky"
        >
          {/* Outer glow ring */}
          <div className="absolute inset-0 rounded-2xl bg-indigo-500/20 blur-xl group-hover:bg-indigo-400/30 transition-all animate-pulse"></div>
          
          {/* Button content */}
          <div className="relative glass-panel px-4 sm:px-5 py-2 sm:py-3 rounded-xl sm:rounded-2xl border-2 border-indigo-500/40 group-hover:border-indigo-400/60 transition-all group-hover:scale-105 flex items-center gap-2 sm:gap-3 bg-slate-900/80">
            {/* Animated star icon */}
            <div className="relative">
              <Moon className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-400 group-hover:text-indigo-300 transition-colors" />
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full animate-ping"></div>
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-amber-400 rounded-full"></div>
            </div>
            
            <div className="text-left">
              <span className="text-white font-semibold block text-sm sm:text-base">Enter Temple Sky</span>
              <span className="text-indigo-300/70 text-xs hidden sm:block">View your constellations</span>
            </div>
            
            {/* Arrow indicator */}
            <svg className="w-4 h-4 text-indigo-400 group-hover:translate-x-1 transition-transform hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>

        {/* Journey Selection Portal - Choose Your Path - Mobile responsive */}
        <button
          onClick={() => {
            setIsNavigating(true);
            if (animationIdRef.current) {
              cancelAnimationFrame(animationIdRef.current);
              animationIdRef.current = null;
            }
            setTimeout(() => navigate('/journey-selection'), 50);
          }}
          className="absolute top-32 sm:top-44 right-4 sm:right-6 group z-20"
          style={{ pointerEvents: 'auto' }}
          data-testid="journey-portal-btn"
          title="Choose your learning journey"
        >
          {/* Outer glow ring - blue theme */}
          <div className="absolute inset-0 rounded-2xl bg-blue-500/20 blur-xl group-hover:bg-blue-400/30 transition-all animate-pulse"></div>
          
          {/* Button content */}
          <div className="relative glass-panel px-4 sm:px-5 py-2 sm:py-3 rounded-xl sm:rounded-2xl border-2 border-blue-500/40 group-hover:border-blue-400/60 transition-all group-hover:scale-105 flex items-center gap-2 sm:gap-3 bg-slate-900/80">
            {/* Compass icon with glow */}
            <div className="relative">
              <Compass className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400 group-hover:text-blue-300 transition-colors" />
              <div className="absolute inset-0 bg-blue-400/30 rounded-full blur-md group-hover:bg-blue-300/40"></div>
            </div>
            
            <div className="text-left">
              <span className="text-white font-semibold block text-sm sm:text-base">Choose Your Path</span>
              <span className="text-blue-300/70 text-xs hidden sm:block">Begin a learning journey</span>
            </div>
            
            {/* Arrow indicator */}
            <svg className="w-4 h-4 text-blue-400 group-hover:translate-x-1 transition-transform hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      </div>

      {/* Daily Quest Banner - Positioned lower on mobile to avoid header overlap */}
      {showQuestBanner && !questBannerDismissed && (
        <div 
          style={{ zIndex: 35, pointerEvents: 'auto' }}
          className="fixed top-44 sm:top-20 left-1/2 -translate-x-1/2 glass-panel px-4 sm:px-6 py-3 sm:py-4 rounded-2xl animate-bounce-slow max-w-[90%] sm:max-w-md"
          data-testid="daily-quest-banner"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-amber-400" />
            </div>
            <div className="flex-1">
              <p className="text-amber-300 font-semibold">A new quest awaits you today!</p>
              <p className="text-sm text-slate-400 mt-1">
                {dailyQuests.filter(q => !q.is_completed).length} quest{dailyQuests.filter(q => !q.is_completed).length !== 1 ? 's' : ''} ready to complete
              </p>
            </div>
            <button
              onClick={() => {
                setShowQuestBanner(false);
                setQuestBannerDismissed(true);
                localStorage.setItem(`questBannerDismissed_${new Date().toDateString()}`, 'true');
              }}
              className="text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Falling Knowledge Star */}
      {fallingStarActive && !fallingStarCaught && (
        <div
          style={{ 
            zIndex: 40, 
            pointerEvents: 'auto',
            position: 'fixed',
            left: `${fallingStarPosition.x}%`,
            top: '0%',
            animation: 'fallingStar 6s linear forwards'
          }}
          className="cursor-pointer"
          onClick={handleCatchFallingStar}
          data-testid="falling-star"
        >
          <div className="relative">
            <div className="w-8 h-8 bg-amber-400 rounded-full animate-pulse shadow-lg shadow-amber-400/50" />
            <div className="absolute inset-0 bg-amber-300 rounded-full animate-ping opacity-75" />
            <Star className="absolute inset-0 m-auto w-5 h-5 text-amber-900" />
          </div>
          <p className="text-amber-300 text-xs mt-1 text-center animate-pulse">Catch me!</p>
        </div>
      )}

      {/* Falling Star Challenge Modal */}
      {fallingStarCaught && fallingStarChallenge && (
        <div 
          style={{ zIndex: 50 }}
          className="fixed inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        >
          <div className="glass-panel p-6 rounded-2xl max-w-md w-full mx-4 animate-scale-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-philosopher font-bold text-white">Knowledge Star!</h3>
                <p className="text-xs text-slate-400">
                  {fallingStarChallenge.type === 'math' ? 'Quick Math' :
                   fallingStarChallenge.type === 'pattern' ? 'Pattern Match' :
                   fallingStarChallenge.type === 'word' ? 'Word Challenge' : 'Rhythm Tap'}
                </p>
              </div>
            </div>
            
            <p className="text-xl text-white text-center mb-6">{fallingStarChallenge.question}</p>
            
            <div className="grid grid-cols-2 gap-3">
              {fallingStarChallenge.options.map((option, i) => (
                <button
                  key={i}
                  onClick={() => handleChallengeAnswer(option)}
                  className="p-3 bg-slate-800/50 hover:bg-amber-500/20 border border-white/10 hover:border-amber-500/50 rounded-xl text-white text-lg transition-all"
                  data-testid={`challenge-option-${i}`}
                >
                  {option}
                </button>
              ))}
            </div>
            
            <p className="text-center text-slate-500 text-sm mt-4">
              +3 bonus sparks for correct answer!
            </p>
          </div>
        </div>
      )}

      {/* Portal Modal */}
      {selectedPortal && (
        <div 
          style={{ zIndex: 30 }}
          className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div 
            className="glass-panel rounded-2xl p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto border-2"
            onClick={(e) => e.stopPropagation()}
            style={{ borderColor: getPortalColorHex(selectedPortal) + '60' }}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = selectedPortal.icon;
                  return <Icon className="w-8 h-8" style={{ color: getPortalColorHex(selectedPortal) }} />;
                })()}
                <h2 className="text-2xl font-bold text-white">
                  {selectedPortal.name}
                </h2>
              </div>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full"
                data-testid="close-modal-btn"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Quests Section */}
            {portalQuests.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Trophy className="w-5 h-5 text-yellow-400" />
                  <h3 className="text-lg font-semibold text-yellow-400">Active Quests</h3>
                </div>
                <div className="space-y-2">
                  {portalQuests.map((questDoc) => (
                    <button
                      key={questDoc.quest_id}
                      onClick={() => handleQuestClick(questDoc.quest, questDoc)}
                      disabled={lessons.length === 0 || loadingLessons}
                      className="w-full text-left p-4 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 hover:from-yellow-500/30 hover:to-orange-500/30 transition-all border border-yellow-500/30 hover:border-yellow-500/50 disabled:opacity-50"
                      data-testid={`quest-${questDoc.quest_id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-white font-semibold mb-1">{questDoc.quest?.title}</h4>
                          <p className="text-slate-300 text-sm">{questDoc.quest?.description}</p>
                        </div>
                        <div className="flex items-center gap-1 text-yellow-400 text-sm bg-yellow-500/20 px-2 py-1 rounded-full">
                          <Sparkles className="w-3 h-3" />
                          <span>+{questDoc.quest?.reward_amount || 10}</span>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Learning Paths Section */}
            {portalPaths.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <Route className="w-5 h-5 text-purple-400" />
                  <h3 className="text-lg font-semibold text-purple-400">Learning Paths</h3>
                </div>
                <div className="space-y-3">
                  {portalPaths.map((path) => {
                    const progress = getPathProgress(path);
                    // Find the first incomplete lesson in this path to start/continue
                    const pathLessons = lessons.filter(l => path.lesson_ids?.includes(l.id));
                    const nextLesson = pathLessons.find(l => 
                      !completedLessons.includes(l.id)
                    ) || pathLessons[0];
                    
                    return (
                      <button
                        key={path.id}
                        onClick={() => {
                          if (progress.isComplete) {
                            toast.info('All lessons in this path are complete!');
                          } else if (nextLesson) {
                            handleLessonClick(nextLesson);
                          } else {
                            toast.info('No lessons available in this path yet');
                          }
                        }}
                        className={`w-full p-4 rounded-xl border transition-all text-left hover:scale-[1.02] ${
                          progress.isComplete 
                            ? 'bg-green-500/10 border-green-500/30 hover:border-green-500/50' 
                            : 'bg-purple-500/10 border-purple-500/30 hover:border-purple-500/50'
                        }`}
                        data-testid={`path-${path.id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-semibold flex items-center gap-2">
                            {progress.isComplete && <CheckCircle className="w-4 h-4 text-green-400" />}
                            {path.title}
                          </h4>
                          <span className="text-purple-400 text-sm">
                            {progress.completed}/{progress.total}
                          </span>
                        </div>
                        {path.description && (
                          <p className="text-slate-400 text-sm mb-3">{path.description}</p>
                        )}
                        {/* Progress bar */}
                        <div className="h-2 bg-slate-800 rounded-full overflow-hidden mb-2">
                          <div 
                            className={`h-full transition-all ${
                              progress.isComplete ? 'bg-green-500' : 'bg-purple-500'
                            }`}
                            style={{ width: `${progress.percent}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-slate-500">{progress.percent}% complete</span>
                          <span className="text-yellow-400 flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            +{path.spark_reward} on completion
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Lessons List */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-5 h-5 text-slate-400" />
                <h3 className="text-lg font-semibold text-slate-300">Lessons</h3>
              </div>
              {loadingLessons ? (
                <div className="text-center py-8">
                  <div className="animate-spin w-8 h-8 border-2 border-white/20 border-t-white rounded-full mx-auto mb-3"></div>
                  <p className="text-slate-400">Loading lessons...</p>
                </div>
              ) : lessons.length > 0 ? (
                <>
                  <p className="text-slate-500 text-sm mb-3">
                    {lessons.length} lesson{lessons.length !== 1 ? 's' : ''} available
                  </p>
                  {lessons.map((lesson, index) => (
                    <button
                      key={lesson.id}
                      onClick={() => handleLessonClick(lesson)}
                      className="w-full text-left p-4 rounded-xl bg-slate-800/50 hover:bg-slate-700/50 transition-all border border-white/5 hover:border-white/20 tv-card focus:outline-none"
                      data-testid={`lesson-${lesson.id}`}
                      data-nav-item
                      tabIndex={0}
                    >
                      <h3 className="text-white font-semibold mb-1">{lesson.title}</h3>
                      {lesson.description && (
                        <p className="text-slate-400 text-sm line-clamp-2">{lesson.description}</p>
                      )}
                    </button>
                  ))}
                </>
              ) : (
                <div className="text-center py-6">
                  <p className="text-slate-400">No lessons available yet</p>
                  <p className="text-slate-500 text-sm mt-2">
                    Ask a parent to create lessons for this room!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Beta Feedback Button */}
      <FeedbackButton childName={selectedChild?.name} childId={selectedChild?.id} />
      
      {/* NEW First-Time Onboarding (comprehensive flow with avatar, path, difficulty) */}
      {showFirstTimeOnboarding && selectedChild && (
        <FirstTimeOnboarding 
          child={selectedChild}
          onComplete={handleFirstTimeOnboardingComplete}
          onSkip={handleFirstTimeOnboardingSkip}
        />
      )}
      
      {/* Legacy Onboarding Overlay - First-time user walkthrough (keeping for transition) */}
      {showOnboarding && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center"
          style={{ pointerEvents: 'auto' }}
          data-testid="onboarding-overlay"
        >
          {/* Semi-transparent backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm"></div>
          
          {/* Onboarding Card */}
          <div className="relative max-w-md mx-4 animate-fade-in">
            {/* Progress dots */}
            <div className="flex justify-center gap-2 mb-4">
              {ONBOARDING_STEPS.map((_, idx) => (
                <div 
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === onboardingStep 
                      ? 'bg-cyan-400 w-6' 
                      : idx < onboardingStep 
                        ? 'bg-cyan-600' 
                        : 'bg-slate-600'
                  }`}
                />
              ))}
            </div>
            
            {/* Card content */}
            <div className="glass-panel p-6 sm:p-8 rounded-3xl border-2 border-cyan-500/30 bg-slate-900/95">
              {/* Step icon/emoji */}
              <div className="text-5xl sm:text-6xl text-center mb-4">
                {onboardingStep === 0 && '🏛️'}
                {onboardingStep === 1 && '📚'}
                {onboardingStep === 2 && '🎮'}
                {onboardingStep === 3 && '⭐'}
                {onboardingStep === 4 && '🚀'}
              </div>
              
              {/* Title */}
              <h2 className="text-xl sm:text-2xl font-bold text-white text-center mb-3">
                {ONBOARDING_STEPS[onboardingStep].title}
              </h2>
              
              {/* Description */}
              <p className="text-slate-300 text-center text-base sm:text-lg mb-6 leading-relaxed">
                {ONBOARDING_STEPS[onboardingStep].description}
              </p>
              
              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                {/* Skip button - not shown on final step */}
                {!ONBOARDING_STEPS[onboardingStep].isFinal && (
                  <button
                    onClick={handleOnboardingSkip}
                    className="px-6 py-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800/50 transition-all text-sm"
                    data-testid="onboarding-skip"
                  >
                    Skip tour
                  </button>
                )}
                
                {/* Next / Start button */}
                <button
                  onClick={handleOnboardingNext}
                  className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold rounded-full transition-all transform hover:scale-105 shadow-lg shadow-cyan-500/30"
                  data-testid="onboarding-next"
                >
                  {ONBOARDING_STEPS[onboardingStep].isFinal 
                    ? "Start Exploring!" 
                    : "Next →"
                  }
                </button>
              </div>
            </div>
            
            {/* Helpful pointer arrow for highlighted elements */}
            {ONBOARDING_STEPS[onboardingStep].highlight === 'portal-nav' && (
              <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-cyan-400 animate-bounce">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 16l-6-6h12z"/>
                </svg>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplePage;
